import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Copy } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { formatAmount } from "@/utils/formatAmount";
import { aiPricePreview, downloadJobPdf, getManagedFileUrl } from "../api";
import {
  useAiPriceInput,
  useAiPricePosition,
  useAiPriceUnmatched,
  useAiReviewRun,
  useAiReviewClear,
  useCatalogEntrySearch,
  useCatalogs,
  useCompleteJob,
  useAbandonJob,
  useSubmitJob,
  useCustomPriceMutations,
  useCustomPrices,
  useExpandNormsJob,
  useJob,
  useManualInputs,
  useManualPositions,
  useSetJobBidVariant,
  useSetJobNote,
  useSetJobOutcome,
  useSetJobSubmittedAmount,
  useSetJobTenderInfo,
  useShareJob,
  useShareableUsers,
  useMatchInput,
  useMatchPosition,
  useNormSearch,
  useRecomputeJob,
  useSetInputComment,
  useSetInputDescription,
  useSetInputKind,
  useSetInputNorm,
  useSetInputExcluded,
  useSetInvestorSupplied,
  useSetManualPrice,
  useSetPositionBasis,
  useSetPositionCategory,
  useSetPositionComment,
  useSetPositionDescription,
  useSetPositionInvestorSupplied,
  useRescanInvestorSupply,
  useSetPositionManualPrice,
  useSetPositionQuantity,
  useSetPositionSoilCategory,
  useSetPositionWholeMarkup,
  useSetRouteLength,
  useTaskPolling,
} from "../hooks";
import type {
  AiPriceFields,
  AiReview,
  CategoryShare,
  CustomPriceEntry,
  EstimateDocument,
  EstimateJobDetail,
  EstimateResult,
  ExpansionWarning,
  JobInvestorSupply,
  ManualPricingMode,
  NormVariant,
  Position,
  PositionCategory,
  PositionInput,
  ReviewStatus,
  TenderOutcome,
  Totals,
  Variant,
  VariantTotals,
} from "../types";
import { CATEGORY_LABELS, OUTCOME_LABELS } from "../types";
import { deadlineLabel, deadlineToneClass, toDateTimeLocal } from "../deadline";
import { SubcontractorSection } from "../components/SubcontractorSection";
import { SummaryTables } from "../components/SummaryTables";
import { SoilCascadeDialog } from "../components/SoilCascadeDialog";
import { ChangeHistoryCard } from "../components/ChangeHistoryCard";
import { DeviceCheckCard } from "../components/DeviceCheckCard";
import { OutagesCard } from "../components/OutagesCard";
import { TakeoffReportCard } from "../components/TakeoffReportCard";
import { downloadBlob, triggerBrowserDownload } from "@/utils/download";

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

// AI market-price recommendation (web search + sources) shown under a nakład/pozycja.
function AiPriceCard({
  obj,
  onClear,
  clearing,
}: {
  obj: AiPriceFields;
  // Undefined for a read-only (shared) viewer — the price stays visible, the
  // "usuń" affordance does not.
  onClear?: () => void;
  clearing?: boolean;
}) {
  if (obj.ai_price == null) return null;
  const range =
    obj.ai_price_min || obj.ai_price_max
      ? ` (${money(obj.ai_price_min)}–${money(obj.ai_price_max)})`
      : "";
  return (
    <div className="mt-1 border-l-2 border-amber-400 pl-2 text-[10px]">
      <div>
        <span className="text-amber-700 font-medium">
          cena rynkowa AI: {money(obj.ai_price)} zł{range}
        </span>
        <span className="ml-1 text-[var(--color-text-muted)]">
          · pewność: {obj.ai_price_confidence || "?"}
        </span>
        {onClear && (
          <button
            className="ml-1.5 text-[var(--color-primary)] hover:underline"
            disabled={clearing}
            onClick={onClear}
          >
            usuń
          </button>
        )}
      </div>
      {obj.ai_price_basis && (
        <div className="text-[var(--color-text-secondary)]">{obj.ai_price_basis}</div>
      )}
      {obj.ai_price_sources?.length > 0 && (
        <div className="text-[var(--color-text-muted)]">
          źródła:{" "}
          {obj.ai_price_sources.map((s, i) => (
            <span key={i}>
              {i > 0 && ", "}
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                title={s.snippet || s.title}
                className="text-[var(--color-primary)] hover:underline"
              >
                {hostname(s.url)}
              </a>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Category → badge colours (consistent with the cost-breakdown panel + PDF).
const CATEGORY_STYLE: Record<PositionCategory, { tag: string; bar: string }> = {
  budowlana: { tag: "bg-amber-100 text-amber-800", bar: "bg-amber-500" },
  ppn: { tag: "bg-blue-100 text-blue-800", bar: "bg-blue-500" },
  biuro: { tag: "bg-violet-100 text-violet-800", bar: "bg-violet-500" },
  inne: { tag: "bg-gray-200 text-gray-600", bar: "bg-gray-400" },
};
const CATEGORY_ORDER: PositionCategory[] = ["budowlana", "ppn", "biuro", "inne"];

interface MatchCtx {
  jobId: string;
  rmsCatalogId: string | null;
  bcjCatalogId: string | null;
  // Author or CEO. A shared valuation is read-only — the backend rejects writes,
  // so hide the edit affordances instead of showing buttons that end in a 403.
  canEdit: boolean;
  // Narzuty z migawki zlecenia — pokazujemy je przy trybie „dolicz Kp + Z”
  // pozycji dopisywanej ręcznie, żeby user wiedział, co dokładnie doliczamy.
  kpPct: string;
  zPct: string;
  // Wariant wartości przetargowej złożony jako oferta — kwoty pozycji, ceny
  // jednostkowe i dowody rozpisujemy z niego, nie zawsze ze średniej.
  variant: Variant;
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (n === 1) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

function MatchPicker({
  catalogId,
  kind,
  onPick,
  onClose,
}: {
  catalogId: string | null;
  kind: string;
  onPick: (entryId: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const { data: results = [], isFetching } = useCatalogEntrySearch(
    catalogId,
    kind,
    q,
  );
  // „Nasza baza cen” — user-curated entries, searchable next to the catalog
  // (RMS kinds only; BCJ work units have no custom counterpart).
  const isRmsKind = kind === "material" || kind === "equipment";
  const own = useCustomPrices(q, isRmsKind ? kind : "", isRmsKind && q.trim().length >= 2);
  const ownResults = isRmsKind ? (own.data ?? []) : [];
  if (!catalogId && !isRmsKind) {
    return (
      <p className="mt-1 text-[11px] text-amber-600">
        Brak cennika do ręcznego dopasowania.
      </p>
    );
  }
  return (
    <div className="mt-1 border border-[var(--color-border)] p-2 bg-[var(--color-bg-base)]">
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Szukaj w cenniku (min. 2 znaki)…"
          className="flex-1 border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[11px] px-2 py-1"
        />
        <button
          className="text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
          onClick={onClose}
        >
          Zamknij
        </button>
      </div>
      {q.trim().length >= 2 && (
        <ul className="mt-1 max-h-48 overflow-auto divide-y divide-[var(--color-border)]">
          {isFetching && (
            <li className="text-[11px] text-[var(--color-text-muted)] py-1">
              Szukam…
            </li>
          )}
          {!isFetching && results.length === 0 && ownResults.length === 0 && (
            <li className="text-[11px] text-[var(--color-text-muted)] py-1">
              Brak wyników.
            </li>
          )}
          {ownResults.map((e) => (
            <li key={e.id}>
              <button
                className="w-full text-left text-[11px] py-1 hover:bg-[var(--color-bg-elevated)]"
                onClick={() => onPick(e.id)}
              >
                <span className="inline-block px-1 mr-1 text-[9px] font-medium bg-emerald-100 text-emerald-800">
                  nasza baza
                </span>
                <span className="font-mono">{e.code}</span> {e.description}
                {e.price_avg != null && (
                  <span className="text-[var(--color-text-muted)]">
                    {" "}
                    — śr {e.price_avg} zł
                  </span>
                )}
              </button>
            </li>
          ))}
          {results.map((e) => (
            <li key={e.id}>
              <button
                className="w-full text-left text-[11px] py-1 hover:bg-[var(--color-bg-elevated)]"
                onClick={() => onPick(e.id)}
              >
                <span className="font-mono">{e.code}</span> {e.description}
                {e.price_avg != null && (
                  <span className="text-[var(--color-text-muted)]">
                    {" "}
                    — śr {e.price_avg} zł
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CatalogBrowser() {
  const { data: catalogs = [] } = useCatalogs();
  const [catalogId, setCatalogId] = useState<string>("");
  const [kind, setKind] = useState<string>("");
  const [q, setQ] = useState("");
  useEffect(() => {
    if (catalogs.length && !catalogId) setCatalogId(catalogs[0].id);
  }, [catalogs, catalogId]);
  const cat = catalogs.find((c) => c.id === catalogId);
  const isRms = cat?.kind === "rms";
  const { data: results = [], isFetching } = useCatalogEntrySearch(
    catalogId || null,
    kind,
    q,
  );
  const ready = q.trim().length >= 2;
  return (
    <div className="card p-4">
      <h2 className="section-label mb-2">Przeglądarka cenników</h2>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <select
          value={catalogId}
          onChange={(e) => {
            setCatalogId(e.target.value);
            setKind("");
          }}
          className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1"
        >
          {catalogs.map((c) => (
            <option key={c.id} value={c.id}>
              {c.source_label} — {c.kind_display} ({c.entry_count})
            </option>
          ))}
        </select>
        {isRms && (
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1"
          >
            <option value="">wszystkie</option>
            <option value="labor">Robocizna (R)</option>
            <option value="material">Materiały (M)</option>
            <option value="equipment">Sprzęt (S)</option>
          </select>
        )}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Szukaj po nazwie lub kodzie…"
          className="flex-1 min-w-[220px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1"
        />
      </div>
      <ul className="max-h-80 overflow-auto divide-y divide-[var(--color-border)] text-[11px]">
        {!ready && (
          <li className="py-2 text-[var(--color-text-muted)]">
            Wpisz min. 2 znaki, aby szukać.
          </li>
        )}
        {ready && isFetching && (
          <li className="py-2 text-[var(--color-text-muted)]">Szukam…</li>
        )}
        {ready && !isFetching && results.length === 0 && (
          <li className="py-2 text-[var(--color-text-muted)]">Brak wyników.</li>
        )}
        {results.map((e) => (
          <li key={e.id} className="py-1 flex justify-between gap-4">
            <span className="min-w-0">
              <span className="font-mono">{e.code}</span> {e.description}
            </span>
            <span className="text-[var(--color-text-muted)] tabular-nums whitespace-nowrap">
              {e.price_avg != null ? `śr ${money(e.price_avg)}` : "—"}
              {e.price_min != null &&
                ` (${money(e.price_min)}–${money(e.price_max)})`}{" "}
              zł
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Wykaz „dostawa inwestorska obejmuje…" odczytany ze SPECYFIKACJI — kosztorys
 *  nakładczy zwykle go nie powtarza, więc bez tego kable i zamki inwestora
 *  wchodziły do oferty wycenione z cennika. Pokazujemy cytat, bo to on jest
 *  podstawą wyzerowania, oraz pozycje wykazu BEZ odpowiednika w kosztorysie —
 *  tych AI świadomie nie dopasowało i wymagają oka kosztorysanta. */
function InvestorSupplyDeclaration({
  job,
  canEdit,
}: {
  job: EstimateJobDetail;
  canEdit: boolean;
}) {
  const supply = job.investor_supply as JobInvestorSupply;
  const rescan = useRescanInvestorSupply();
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const qc = useQueryClient();

  useEffect(() => {
    const s = task.data?.status;
    if (s && ["success", "failure", "cancelled"].includes(s)) {
      if (s === "success")
        qc.invalidateQueries({ queryKey: ["est", "job", job.id] });
      setTaskId(null);
    }
  }, [task.data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = supply?.items ?? [];
  const running = !!taskId || rescan.isPending;

  return (
    <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="section-label">Wykaz ze specyfikacji</span>
        {canEdit && (
          <Button
            variant="secondary"
            className="text-[11px] px-2 py-1"
            disabled={running}
            onClick={async () => {
              const res = await rescan.mutateAsync(job.id);
              setTaskId(res.task_id);
            }}
            title="Przeszukaj specyfikację pod kątem wykazu dostawy inwestorskiej i wyzeruj pasujące nakłady"
          >
            {running ? "Szukam…" : "Przeszukaj specyfikację"}
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
          {supply?.declared === "yes"
            ? "Deklaracja jest, ale bez wykazu materiałów."
            : "W dokumentach nie znaleziono wykazu dostawy inwestorskiej."}
        </p>
      ) : (
        <ul className="mt-1 space-y-1.5 text-[11px]">
          {items.map((it, i) => {
            const matched = it.matched ?? [];
            return (
              <li key={`${it.name}-${i}`}>
                <div className="text-[var(--color-text-primary)]">
                  <span className="font-medium">{it.name}</span>
                  {it.quantity && (
                    <span className="text-[var(--color-text-muted)]">
                      {" "}
                      — {it.quantity} {it.unit}
                    </span>
                  )}
                  {matched.length === 0 && (
                    <span
                      className="ml-2 px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-900"
                      title="AI nie znalazło w kosztorysie nakładu, który byłby tą samą rzeczą — sprawdź ręcznie"
                    >
                      bez odpowiednika w kosztorysie
                    </span>
                  )}
                </div>
                {it.quote && (
                  <div className="text-[var(--color-text-muted)]">
                    „{it.quote}” — {it.filename}
                    {it.page ? `, s. ${it.page}` : ""}
                  </div>
                )}
                {it.quantity_note && (
                  <div className="text-amber-800">⚠ {it.quantity_note}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function InvestorList({
  job,
  canEdit,
}: {
  job: EstimateJobDetail;
  canEdit: boolean;
}) {
  const investor = useSetInvestorSupplied(job.id);
  const posInvestor = useSetPositionInvestorSupplied(job.id);
  // Dostawa inwestorska ma DWA poziomy: cała pozycja (zeruje ją w całości, także
  // z nakładami) albo pojedynczy nakład. Pozycja oznaczona w całości zastępuje
  // swoje nakłady jednym wpisem — inaczej ta sama dostawa figurowałaby tu N razy.
  const items: {
    id: string;
    description: string;
    lp: string;
    whole: boolean;
  }[] = [];
  job.estimates.forEach((est) =>
    est.positions.forEach((p) => {
      const lp = p.ordinal_label || String(p.ordinal);
      if (p.investor_supplied) {
        items.push({
          id: p.id,
          description: p.description || p.basis_raw,
          lp,
          whole: true,
        });
        return;
      }
      p.inputs.forEach((inp) => {
        if (inp.investor_supplied)
          items.push({
            id: inp.id,
            description: inp.description,
            lp,
            whole: false,
          });
      });
    }),
  );
  return (
    <div className="card p-4">
      <h2 className="section-label mb-2">
        Uznane dostawy inwestorskie (wycenione na 0 zł)
      </h2>
      {items.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">
          Brak — żadna pozycja nie została uznana za dostawę inwestorską.
        </p>
      ) : (
        <ul className="divide-y divide-[var(--color-border)] text-sm">
          {items.map((it) => (
            <li
              key={it.id}
              className="py-1.5 flex items-center justify-between gap-3"
            >
              <span>
                <span className="text-[var(--color-text-muted)]">
                  poz. {it.lp}:
                </span>{" "}
                {it.description}
                {it.whole && (
                  <span className="ml-2 text-[11px] text-[var(--color-text-muted)]">
                    (cała pozycja)
                  </span>
                )}
              </span>
              {canEdit && (
                <button
                  className="text-[11px] text-[var(--color-primary)] hover:underline whitespace-nowrap"
                  onClick={() =>
                    it.whole
                      ? posInvestor.mutate({ positionId: it.id, value: false })
                      : investor.mutate({ inputId: it.id, value: false })
                  }
                >
                  to nie jest inwestorska — przywróć wycenę
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
        Wykrywane automatycznie (skreślone pozycje w kosztorysie + wykaz ze
        specyfikacji), zachowawczo. Pominięte oznacz w „Szczegółach” pozycji
        przyciskiem „inwestorska”.
      </p>
      <InvestorSupplyDeclaration job={job} canEdit={canEdit} />
    </div>
  );
}

function AiCostBar({ job }: { job: EstimateJobDetail }) {
  if (!job.ai_calls) return null;
  const usd = Number(job.ai_cost_usd || 0);
  return (
    <div className="card p-3 text-[11px] text-[var(--color-text-muted)] flex flex-wrap gap-x-5 gap-y-1">
      <span>
        Analiza AI — model:{" "}
        <span className="font-mono text-[var(--color-text-secondary)]">
          {job.ai_model || "—"}
        </span>
      </span>
      <span>{job.ai_calls} wywołań</span>
      <span>
        {job.ai_input_tokens.toLocaleString("pl-PL")} tok. wejściowych /{" "}
        {job.ai_output_tokens.toLocaleString("pl-PL")} tok. wyjściowych
      </span>
      <span>
        koszt ≈{" "}
        <span className="text-[var(--color-text-secondary)]">
          ${usd.toFixed(4)}
        </span>{" "}
        (≈ {money(String(usd * 4))} zł)
      </span>
    </div>
  );
}

function CategoryBreakdown({ job }: { job: EstimateJobDetail }) {
  const short = VARIANT_SHORT[job.bid_variant ?? "avg"];
  const bd = (job.category_breakdown ?? {}) as Partial<
    Record<PositionCategory, CategoryShare>
  >;
  const analytics = job.analytics as {
    route_length_m?: string | null;
    route_open_trench_m?: string | null;
    route_bore_m?: string | null;
    line_rate_per_m?: string | null;
  };
  const hasAny = CATEGORY_ORDER.some((c) => bd[c] && Number(bd[c]?.value) > 0);
  if (!hasAny) return null;
  return (
    <div className="card p-4">
      <h2 className="section-label mb-3">
        Podział kosztów wg kategorii ({short})
      </h2>
      <div className="space-y-2">
        {CATEGORY_ORDER.map((c) => {
          const share = bd[c];
          const pct = share ? Number(share.pct) : 0;
          return (
            <div key={c} className="flex items-center gap-3 text-xs">
              <span
                className={`inline-flex justify-center w-20 px-1.5 py-0.5 font-medium ${CATEGORY_STYLE[c].tag}`}
              >
                {CATEGORY_LABELS[c]}
              </span>
              <div className="flex-1 h-3 bg-[var(--color-bg-elevated)] overflow-hidden">
                <div
                  className={`h-full ${CATEGORY_STYLE[c].bar}`}
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
              <span className="w-12 text-right tabular-nums text-[var(--color-text-secondary)]">
                {pct.toFixed(1)}%
              </span>
              <span className="w-28 text-right tabular-nums font-medium text-[var(--color-text-primary)]">
                {money(share?.value)} zł
              </span>
            </div>
          );
        })}
      </div>
      {analytics?.route_length_m && (
        <div className="mt-3 pt-3 border-t border-[var(--color-border)] text-xs text-[var(--color-text-secondary)] tabular-nums">
          Trasa (długość trasowa):{" "}
          {analytics.route_open_trench_m && (
            <>wykop otwarty {money(analytics.route_open_trench_m)} mb · </>
          )}
          {analytics.route_bore_m && (
            <>przewierty {money(analytics.route_bore_m)} mb · </>
          )}
          razem{" "}
          <span className="font-semibold text-[var(--color-text-primary)]">
            {money(analytics.route_length_m)} mb
          </span>{" "}
          · stawka{" "}
          <span className="font-semibold text-[var(--color-primary)]">
            {money(analytics.line_rate_per_m)} zł/mb
          </span>
        </div>
      )}
    </div>
  );
}

// Headless AI review ("presetowany ARON") — per-category status styling + ordering.
const REVIEW_STATUS_STYLE: Record<
  ReviewStatus,
  { tag: string; border: string; label: string; order: number }
> = {
  error: {
    tag: "bg-red-100 text-red-800",
    border: "border-red-400",
    label: "Błąd",
    order: 0,
  },
  warning: {
    tag: "bg-amber-100 text-amber-800",
    border: "border-amber-400",
    label: "Ostrzeżenie",
    order: 1,
  },
  ok: {
    tag: "bg-emerald-100 text-emerald-800",
    border: "border-emerald-300",
    label: "OK",
    order: 2,
  },
};

function AiReviewPanel({
  job,
  canEdit,
}: {
  job: EstimateJobDetail;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const runReview = useAiReviewRun(job.id);
  const clearReview = useAiReviewClear(job.id);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const task = useTaskPolling(taskId);
  const data =
    job.ai_review && "checks" in job.ai_review ? (job.ai_review as AiReview) : null;
  // Run is now a background task — show "running" while it's queued OR polling.
  const running = !!taskId || runReview.isPending;

  useEffect(() => {
    const s = task.data?.status;
    if (!s || !["success", "failure", "cancelled"].includes(s)) return;
    // Zadanie kończy się sukcesem także wtedy, gdy przegląd NIE powstał (brak
    // klucza, nieudane wywołanie modelu) — zwraca wtedy {ok:false}. Bez tej gałęzi
    // spinner po prostu gaśnie, panel wygląda jak przed kliknięciem i użytkownik
    // widzi „chwilę myśli i nic się nie dzieje”, zamiast powodu.
    const ok = (task.data?.result as { ok?: boolean } | null)?.ok;
    if (s === "success" && ok !== false) {
      qc.invalidateQueries({ queryKey: ["est", "job", job.id] });
    } else {
      const reason = (task.data?.log || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .pop();
      setErr(reason || "Przegląd AI nie powiódł się.");
    }
    setTaskId(null);
  }, [task.data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setErr("");
    try {
      const r = await runReview.mutateAsync();
      setTaskId(r.task_id);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { errors?: string[] } } })?.response
        ?.data?.errors?.[0];
      setErr(msg || "Przegląd AI nie powiódł się.");
    }
  };

  const clear = async () => {
    setErr("");
    try {
      await clearReview.mutateAsync();
    } catch {
      setErr("Nie udało się wyczyścić audytu.");
    }
  };

  const checks = [...(data?.checks ?? [])].sort(
    (a, b) => REVIEW_STATUS_STYLE[a.status].order - REVIEW_STATUS_STYLE[b.status].order,
  );
  const counts = data?.counts ?? { ok: 0, warning: 0, error: 0 };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="section-label">Przegląd AI — audyt wyceny</h2>
        {canEdit && (
          <div className="flex items-center gap-3">
            {data && (
              <button
                className="text-xs text-[var(--color-text-muted)] hover:underline"
                disabled={running || clearReview.isPending}
                onClick={clear}
              >
                wyczyść
              </button>
            )}
            <Button
              variant="secondary"
              size="sm"
              loading={running}
              disabled={job.estimates.length === 0}
              onClick={run}
              title="ARON audytuje wycenę po kategoriach: kompletność, kategorie, pozycje 0 zł, ceny rynkowe (nasze i podwykonawcze), trasa, marża/pokrycie, dojazd, zgodność z bazą wiedzy PRZ-ARON"
            >
              {data ? "Odśwież audyt" : "Uruchom audyt AI"}
            </Button>
          </div>
        )}
      </div>

      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}

      {!data && !running && !canEdit && (
        <p className="text-xs text-[var(--color-text-muted)]">
          Autor wyceny nie uruchomił jeszcze audytu AI.
        </p>
      )}

      {!data && !running && canEdit && (
        <p className="text-xs text-[var(--color-text-muted)]">
          Drugie spojrzenie AI na wycenę — ustrukturyzowany audyt po kategoriach (każda z
          oceną OK / Ostrzeżenie / Błąd): kompletność zakresu, poprawność kategorii,
          pozycje zaniżone do 0 zł, ceny rynkowe (nasze i podwykonawcze), długość trasowa,
          marża i pokrycie, dojazd, a przy aktywnych notatkach także zgodność z bazą wiedzy
          PRZ-ARON — plus rekomendacje. Najlepiej uruchom po skonfigurowaniu podwykonawców.
        </p>
      )}

      {data && (
        <>
          {data.consistency_alert && (
            <div className="mb-2 border border-red-300 bg-red-50 p-2 text-xs text-red-800">
              <span className="font-semibold">⚠ Audyt wewnętrznie sprzeczny: </span>
              {data.consistency_alert}
            </div>
          )}
          {data.summary && (
            <p className="text-sm text-[var(--color-text-secondary)] mb-2">
              {data.summary}
            </p>
          )}
          <div className="flex items-center gap-3 mb-3 text-[11px] tabular-nums">
            <span className="text-emerald-700">OK: {counts.ok}</span>
            <span className="text-amber-700">ostrzeżenia: {counts.warning}</span>
            <span className="text-red-700">błędy: {counts.error}</span>
          </div>
          <div className="space-y-2">
            {checks.map((c) => {
              const st = REVIEW_STATUS_STYLE[c.status];
              return (
                <div key={c.category} className={`border-l-2 ${st.border} pl-3 py-1`}>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span
                      className={`inline-flex px-1.5 py-0.5 font-medium ${st.tag}`}
                    >
                      {st.label}
                    </span>
                    <span className="font-medium text-[var(--color-text-primary)]">
                      {c.label}
                    </span>
                    {c.ordinals.length > 0 && (
                      <span className="text-[var(--color-text-muted)]">
                        poz. {c.ordinals.join(", ")}
                      </span>
                    )}
                  </div>
                  {c.summary && (
                    <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
                      {c.summary}
                    </p>
                  )}
                  {c.recommendation && (
                    <p className="text-xs text-[var(--color-primary)] mt-0.5">
                      → {c.recommendation}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {data.advice.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
              <div className="section-label mb-1">Rekomendacje</div>
              <ul className="list-disc pl-5 space-y-0.5 text-xs text-[var(--color-text-secondary)]">
                {data.advice.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-[10px] text-[var(--color-text-muted)] mt-3">
            Model: {data.model || "—"}
            {data.generated_at &&
              ` · ${new Date(data.generated_at).toLocaleString("pl-PL")}`}{" "}
            · audyt AI — zweryfikuj przed użyciem.
          </p>
        </>
      )}
    </div>
  );
}

/** Cena z wyceny AI w danym wariancie — tak samo jak liczy ją backend: min/maks
 *  spadają do ceny średniej, gdy AI nie podało widełek. */
function aiPriceFor(obj: AiPriceFields, variant: Variant): string | null {
  if (variant === "min") return obj.ai_price_min ?? obj.ai_price;
  if (variant === "max") return obj.ai_price_max ?? obj.ai_price;
  return obj.ai_price;
}

/** Skrót wariantu do etykiet kolumn i nagłówków („Wartość śr.”). */
const VARIANT_SHORT: Record<Variant, string> = {
  min: "min.",
  avg: "śr.",
  max: "maks.",
};

const VARIANTS: { key: Variant; label: string }[] = [
  { key: "min", label: "Minimalna" },
  { key: "avg", label: "Średnia" },
  { key: "max", label: "Maksymalna" },
];

function money(v?: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  // formatAmount always groups thousands; pl-PL locale skips grouping for 4-digit
  // numbers (minimumGroupingDigits=2), which produced "1191,01" next to "10 130,30".
  return formatAmount(n);
}

/** Kopiuje kwotę w postaci gotowej do wklejenia w formularz ofertowy
 *  ("120117,97" — przecinek dziesiętny, bez spacji w tysiącach) i mruga
 *  potwierdzeniem. Ten sam format co przy propozycjach wyceny przetargów. */
function CopyAmountButton({ value }: { value?: string | null }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const n = Number(value);
    const raw = Number.isNaN(n) ? String(value ?? "") : n.toFixed(2).replace(".", ",");
    if (!raw) return;
    navigator.clipboard
      ?.writeText(raw)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      // Schowek bywa zablokowany (brak HTTPS / uprawnień) — kwota jest widoczna,
      // więc nie robimy z tego błędu na wierzchu.
      .catch(() => undefined);
  };
  if (value == null || value === "") return null;
  return (
    <button
      type="button"
      onClick={copy}
      title="Skopiuj kwotę do wklejenia w formularz ofertowy"
      className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[11px] border transition-colors ${
        copied
          ? "border-emerald-500 text-emerald-700 bg-emerald-50"
          : "border-[var(--color-border)] text-[var(--color-text-secondary)] bg-[var(--color-bg-surface)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
      }`}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? "Skopiowano" : "Kopiuj"}
    </button>
  );
}

/** Kwota oferty do ręcznej poprawki (tylko status „Złożony”). Na formularzu
 *  ofertowym kwota bywa zaokrąglona albo dobita po naradzie — zapisujemy to, co
 *  naprawdę poszło do zamawiającego, zamiast czytać wariant, który zmieni się
 *  przy najbliższym przeliczeniu. Puste pole = wróć do kwoty wariantu. */
function SubmittedAmountInput({
  value,
  onSave,
  saving,
}: {
  /** Surowa kwota z API ("120117.97"). */
  value: string;
  onSave: (v: string) => void;
  saving?: boolean;
}) {
  // Do wpisywania pokazujemy liczbę bez grupowania tysięcy ("120117,97") — z
  // formatowaną wersją każda poprawka wymaga walki ze spacjami. Po wyjściu z pola
  // wraca ładny format, ten sam co na pozostałych kartach.
  const plain = (v: string) => {
    const n = Number(v);
    return Number.isNaN(n) ? v : n.toFixed(2).replace(".", ",");
  };
  const [draft, setDraft] = useState(() => plain(value));
  const [focused, setFocused] = useState(false);
  // Kwota mogła się zmienić spod spodu (zapis, przestawiony wariant) — dociągnij
  // ją, dopóki użytkownik nie pisze, żeby pole nie pokazywało nieaktualnej liczby.
  useEffect(() => {
    if (!focused) setDraft(plain(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused]);
  const commit = () => {
    setFocused(false);
    if (draft.trim() === plain(value)) return;
    onSave(draft);
  };
  return (
    <input
      value={focused ? draft : money(value)}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => {
        setFocused(true);
        setDraft(plain(value));
        requestAnimationFrame(() => e.target.select());
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(plain(value));
          setFocused(false);
          e.currentTarget.blur();
        }
      }}
      disabled={saving}
      inputMode="decimal"
      title="Kwota faktycznie złożona w przetargu — Enter zapisuje, Esc cofa"
      aria-label="Kwota złożonej oferty"
      // Szerokość idzie za treścią: przy stałej kwota siedmiocyfrowa ucinała się,
      // a przy krótkiej zostawała dziura między liczbą a „zł".
      style={{ width: `${Math.max((focused ? draft : money(value)).length, 6) + 0.5}ch` }}
      className="max-w-full bg-transparent border-b border-dashed border-[var(--color-primary)] text-2xl font-semibold tabular-nums text-[var(--color-primary)] focus:outline-none focus:border-solid disabled:opacity-60"
    />
  );
}

/** Pytanie zadawane przy oznaczaniu oferty jako złożonej: z KTÓRĄ kwotą stanęliśmy
 *  do przetargu. Domyślnie wariant wybrany na kartach, ale tu można go zmienić albo
 *  wpisać kwotę z formularza ofertowego (zaokrąglenie, dobicie ceny po naradzie).
 *  Wybór wariantu przestawia też pochodne — jak kliknięcie kafelka. */
function SubmitOfferDialog({
  open,
  onClose,
  totals,
  currentVariant,
  onConfirm,
  pending,
  error,
}: {
  open: boolean;
  onClose: () => void;
  totals: Partial<Totals>;
  currentVariant: Variant;
  onConfirm: (variant: Variant, amount: string) => void;
  pending?: boolean;
  error?: boolean;
}) {
  const [variant, setVariant] = useState<Variant>(currentVariant);
  const [amount, setAmount] = useState("");
  const plain = (v?: string | null) => {
    const n = Number(v);
    return v == null || Number.isNaN(n) ? "" : n.toFixed(2).replace(".", ",");
  };
  // Po otwarciu zaczynamy od tego, co widać na kartach; przełączenie wariantu
  // podstawia jego kwotę, bo to najczęstszy przypadek (kwota = policzony wariant).
  useEffect(() => {
    if (open) {
      setVariant(currentVariant);
      setAmount(plain(totals[currentVariant]?.total));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentVariant]);
  const pick = (v: Variant) => {
    setVariant(v);
    setAmount(plain(totals[v]?.total));
  };
  const changed = amount.trim() !== plain(totals[variant]?.total);
  return (
    <Modal open={open} onClose={onClose} title="Złożenie oferty" size="md">
      <p className="text-xs text-[var(--color-text-secondary)] mb-3">
        Z którą kwotą stanęliśmy do przetargu? Wybrany wariant przestawia też
        podział kosztów, zł/mb, marżę na podwykonawcach i raport PDF.
      </p>
      <div className="space-y-2">
        {VARIANTS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => pick(v.key)}
            className={`w-full p-3 border text-left flex items-center justify-between gap-3 transition-colors ${
              variant === v.key
                ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                : "border-[var(--color-border)] hover:border-[var(--color-primary)]"
            }`}
          >
            <span className="section-label">{v.label}</span>
            <span
              className={`text-lg font-semibold tabular-nums ${
                variant === v.key
                  ? "text-[var(--color-primary)]"
                  : "text-[var(--color-text-primary)]"
              }`}
            >
              {money(totals[v.key]?.total)} zł
            </span>
          </button>
        ))}
      </div>
      <label className="block mt-4">
        <span className="section-label">Kwota złożonej oferty [zł]</span>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          className="mt-1 w-full p-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-lg font-semibold tabular-nums focus:outline-none focus:border-[var(--color-primary)]"
        />
        <span className="block mt-1 text-[11px] text-[var(--color-text-muted)]">
          {changed
            ? `Ręczna kwota — policzony wariant to ${money(totals[variant]?.total)} zł. Zapisujemy Twoją.`
            : "Domyślnie kwota wybranego wariantu — popraw, jeśli w formularzu ofertowym poszła inna."}
        </span>
      </label>
      {error && (
        <p className="mt-2 text-[11px] text-red-600">
          Nie udało się złożyć oferty — sprawdź kwotę (np. 120117,97) i spróbuj
          ponownie.
        </p>
      )}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onClose} disabled={pending}>
          Anuluj
        </Button>
        <Button
          size="sm"
          onClick={() => onConfirm(variant, amount)}
          loading={pending}
        >
          Złóż ofertę
        </Button>
      </div>
    </Modal>
  );
}

/** Jedna z trzech kwot wartości przetargowej. Kliknięcie wybiera wariant, który
 *  składamy jako ofertę — od niego liczy się reszta strony (podział wg kategorii,
 *  zł/mb, marża na podwykonawcach) i raport PDF. */
function SummaryCard({
  label,
  t,
  highlight,
  showRMS,
  onSelect,
  pending,
  submittedAmount,
  onSubmittedAmountSave,
  savingAmount,
}: {
  label: string;
  t?: VariantTotals;
  highlight?: boolean;
  showRMS?: boolean;
  onSelect?: () => void;
  pending?: boolean;
  /** Kwota złożonej oferty do edycji — podana TYLKO na karcie oferty w statusie
   *  „Złożony”. Bez niej karta pokazuje policzoną kwotę wariantu jak dotąd. */
  submittedAmount?: string;
  onSubmittedAmountSave?: (v: string) => void;
  savingAmount?: boolean;
}) {
  const clickable = Boolean(onSelect) && !highlight;
  const editable = Boolean(onSubmittedAmountSave) && submittedAmount != null;
  // Kwota pokazana na karcie: złożona (gdy już wpisana) albo policzony wariant.
  const shown = submittedAmount ?? t?.total ?? null;
  // Ręczna poprawka względem wariantu — pokaż, od czego odbiegła, żeby nikt nie
  // musiał zgadywać, czy to literówka, czy świadome zaokrąglenie.
  const differs =
    submittedAmount != null &&
    t?.total != null &&
    Number(submittedAmount) !== Number(t.total);
  const inner = (
    <>
      <div className="section-label flex items-center justify-between gap-2">
        <span>{label}</span>
        {highlight && (
          <span className="text-[var(--color-primary)] normal-case">
            {editable ? "✓ złożona oferta" : "✓ oferta"}
          </span>
        )}
        {clickable && !pending && (
          <span className="text-[var(--color-text-muted)] normal-case">
            wybierz
          </span>
        )}
        {clickable && pending && (
          <span className="text-[var(--color-text-muted)] normal-case">
            przeliczam…
          </span>
        )}
      </div>
      {/* Przy siedmiocyfrowej kwocie przycisk kopiowania nie mieści się w rzędzie —
          niech zjedzie pod spód, zamiast odrywać „zł" od liczby. */}
      <div className="mt-1 flex flex-wrap items-end justify-between gap-x-2 gap-y-1">
        <div
          className={`whitespace-nowrap text-2xl font-semibold tabular-nums leading-tight ${
            highlight
              ? "text-[var(--color-primary)]"
              : "text-[var(--color-text-primary)]"
          }`}
        >
          {editable ? (
            <SubmittedAmountInput
              value={submittedAmount}
              onSave={onSubmittedAmountSave!}
              saving={savingAmount}
            />
          ) : (
            // Bez prawa edycji (wycena udostępniona do podglądu) kwota złożonej
            // oferty i tak musi być widoczna — inaczej pokazywalibyśmy wariant
            // zamiast tego, co naprawdę poszło do zamawiającego.
            money(shown)
          )}{" "}
          <span className="text-base font-normal text-[var(--color-text-muted)]">
            zł
          </span>
        </div>
        {highlight && <CopyAmountButton value={shown} />}
      </div>
      {showRMS && (
        <div className="text-[11px] text-[var(--color-text-secondary)] mt-1.5 tabular-nums">
          R {money(t?.sum_R)} · M {money(t?.sum_M)} · S {money(t?.sum_S)}
        </div>
      )}
      {differs && (
        <div className="text-[11px] text-[var(--color-text-muted)] mt-1 tabular-nums">
          policzony wariant: {money(t?.total)} zł
        </div>
      )}
    </>
  );
  const className = `p-4 border text-left w-full transition-colors ${
    highlight
      ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
      : "border-[var(--color-border)] bg-[var(--color-bg-surface)]"
  } ${
    clickable
      ? "cursor-pointer hover:border-[var(--color-primary)] disabled:opacity-60"
      : "cursor-default"
  }`;
  // Karta wybrana (i każda w trybie tylko-do-odczytu) nie jest klikalna, więc jest
  // <div> — inaczej przycisk kopiowania i pole kwoty byłyby zagnieżdżone w <button>.
  if (!clickable) {
    return (
      <div
        className={className}
        title={
          highlight
            ? "Ten wariant składamy jako ofertę — od niego liczą się pozostałe wartości"
            : undefined
        }
      >
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      disabled={pending}
      onClick={onSelect}
      title={`Policz wszystko z wariantu: ${label.toLowerCase()}`}
      className={className}
    >
      {inner}
    </button>
  );
}

/** Jeden CZERWONY znacznik na wszystko, co kosztorysant dopisał albo zmienił
 *  ręcznie (pozycja, nakład, ilość nakładu, obmiar, nazwa). Czytający wycenę ma
 *  od razu widzieć, które liczby nie pochodzą z dokumentu ani z cennika. */
function ManualBadge({ label, title }: { label: string; title?: string }) {
  return (
    <span
      className="inline-block px-1 py-px text-[9px] font-semibold border border-red-400 bg-red-50 text-red-700"
      title={title}
    >
      {label}
    </span>
  );
}

/** Szczegół z odpowiedzi backendu ({data, errors:{detail}, meta}) albo tekst zapasowy. */
function errDetail(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { errors?: { detail?: string } } } })?.response?.data
      ?.errors?.detail || fallback
  );
}

function matchLabel(status: string, score: number): string {
  if (status === "manual") return "✓ ręczne";
  if (status === "exact") return "✓ kod";
  if (status === "ai") return `AI ${score.toFixed(0)}%`;
  if (status === "ai_price") return "AI rynek";
  if (status === "fuzzy") return `≈ ${score.toFixed(0)}%`;
  return "⚠ brak";
}

// Resource kind → compact colored tag (R = robocizna, M = materiał, S = sprzęt).
const KIND_TAG: Record<string, { letter: string; cls: string; title: string }> =
  {
    labor: {
      letter: "R",
      cls: "bg-blue-100 text-blue-700",
      title: "Robocizna",
    },
    material: {
      letter: "M",
      cls: "bg-emerald-100 text-emerald-700",
      title: "Materiał",
    },
    equipment: {
      letter: "S",
      cls: "bg-amber-100 text-amber-700",
      title: "Sprzęt",
    },
  };

function KindTag({ kind }: { kind: string }) {
  const k = KIND_TAG[kind] ?? {
    letter: "?",
    cls: "bg-gray-200 text-gray-600",
    title: kind,
  };
  return (
    <span
      title={k.title}
      className={`inline-flex items-center justify-center w-4 h-4 mr-1.5 align-middle text-[9px] font-bold font-mono ${k.cls}`}
    >
      {k.letter}
    </span>
  );
}

// ── Demontaż przez analogię ──────────────────────────────────────────────────
// A przedmiar position described as "Demontaż …" but based on a MONTAŻ/WYMIANA
// norm ("KNNR 5 0803-04 analogia") gets only R+S — the norm's material nakłady
// are struck out during expansion and recorded on position.expansion_warnings.
// The badge marks the position in the collapsed table; the panel explains the
// strike-out in full inside the expanded details.

function demontazWarnings(pos: Position): ExpansionWarning[] {
  return (pos.expansion_warnings ?? []).filter(
    (w) => w.code === "demontaz_analogia",
  );
}

// ── Norma opisuje inne urządzenie ────────────────────────────────────────────
// Zła podstawa w przedmiarze rozwija się bez jednego błędu — po prostu nalicza
// nakłady innego urządzenia (SZR zamiast szafki P3, ~80 tys. zł w plecy). Kod
// nie ma jak tego zauważyć, opis pozycji ma. Badge jest CZERWONY, bo w odróżnieniu
// od demontażu przez analogię to nie konwencja kosztorysowa, tylko podejrzenie błędu.

function deviceMismatchWarnings(pos: Position): ExpansionWarning[] {
  return (pos.expansion_warnings ?? []).filter(
    (w) => w.code === "device_mismatch",
  );
}

// ── Jednostka normy ≠ jednostka pozycji ──────────────────────────────────────
// Nakłady normy liczy się NA JEDNĄ JEDNOSTKĘ pozycji, więc norma „za metr”
// rozwinięta na pozycji „1 kpl.” (geodezja, projekt organizacji ruchu) policzyłaby
// robotę na jeden metr — wycena groszowa, a w rozwinięciu nic nie wygląda na błąd.
// Backend takiej pozycji nie rozwija; tu tłumaczymy dlaczego i co z tym zrobić.

function unitMismatchWarnings(pos: Position): ExpansionWarning[] {
  return (pos.expansion_warnings ?? []).filter((w) => w.code === "unit_mismatch");
}

function UnitMismatchBadge({ w }: { w: ExpansionWarning }) {
  return (
    <span
      className="inline-block px-1 py-px text-[9px] font-semibold border border-red-400 bg-red-50 text-red-700"
      title={w.note}
    >
      ⚠ JM POZYCJI „{w.position_unit || "—"}” ≠ JM NORMY „{w.norm_unit || "—"}”
    </span>
  );
}

function UnitMismatchPanel({ pos }: { pos: Position }) {
  const warnings = unitMismatchWarnings(pos);
  if (warnings.length === 0) return null;
  return (
    <>
      {warnings.map((w, i) => (
        <div
          key={i}
          className="mb-3 border border-red-300 bg-red-50 p-3 text-[11px] text-red-900"
        >
          <p className="font-semibold text-[12px] mb-1.5">
            ⚠ Jednostka pozycji nie zgadza się z jednostką normy — nakładów nie
            rozwinięto
          </p>
          <div className="space-y-0.5 font-mono text-[10.5px] mb-2">
            <p>
              <span className="text-red-700">Pozycja:&nbsp;</span>
              <span className="font-semibold">
                {pos.quantity} {w.position_unit || "—"}
              </span>{" "}
              — {pos.description}
            </p>
            <p>
              <span className="text-red-700">Norma:&nbsp;</span>
              <span className="font-semibold">
                {w.norm_code} / {w.norm_unit || "—"}
              </span>{" "}
              — {w.norm_desc}
            </p>
          </div>
          <p className="leading-relaxed">{w.note}</p>
        </div>
      ))}
    </>
  );
}

// ── Jednostka normy w innej SKALI / nieznana ─────────────────────────────────
// KNR 5-02 podaje nakłady na 1 KM linii kablowej, przedmiar mierzy tę samą trasę
// w METRACH. Rozwinięcie „wprost” mnoży pozycję 1000× (482 zł → 482 304 zł) i nic
// w tabeli nakładów nie wygląda źle. Backend przelicza obmiar na jednostkę normy
// i mówi to tutaj — pozycja jest policzona, ale kosztorysant musi potwierdzić, w
// czym naprawdę podano obmiar. `unit_unknown` = katalog nie wniósł jm normy, więc
// tej kontroli nie było czym zrobić (ta sama luka, tylko cicha).

function unitScaleWarnings(pos: Position): ExpansionWarning[] {
  return (pos.expansion_warnings ?? []).filter(
    (w) => w.code === "unit_scale_mismatch" || w.code === "unit_unknown",
  );
}

function UnitScaleBadge({ w }: { w: ExpansionWarning }) {
  const scale = w.code === "unit_scale_mismatch";
  return (
    <span
      className="inline-block px-1 py-px text-[9px] font-semibold border border-amber-400 bg-amber-50 text-amber-800"
      title={w.note}
    >
      {scale
        ? `⚠ OBMIAR PRZELICZONY „${w.position_unit || "—"}” → „${w.norm_unit || "—"}”`
        : "⚠ NIEZNANA JM NORMY"}
    </span>
  );
}

function UnitScalePanel({ pos }: { pos: Position }) {
  const warnings = unitScaleWarnings(pos);
  if (warnings.length === 0) return null;
  return (
    <>
      {warnings.map((w, i) => (
        <div
          key={i}
          className="mb-3 border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900"
        >
          <p className="font-semibold text-[12px] mb-1.5">
            {w.code === "unit_scale_mismatch"
              ? "⚠ Norma w innej skali niż obmiar — obmiar przeliczono na jednostkę normy"
              : "⚠ Katalog nie podał jednostki normy — nakładów nie dało się skontrolować"}
          </p>
          <div className="space-y-0.5 font-mono text-[10.5px] mb-2">
            <p>
              <span className="text-amber-800">Pozycja:&nbsp;</span>
              <span className="font-semibold">
                {pos.quantity} {w.position_unit || "—"}
              </span>{" "}
              — {pos.description}
            </p>
            <p>
              <span className="text-amber-800">Norma:&nbsp;</span>
              <span className="font-semibold">
                {w.norm_code} / {w.norm_unit || "—"}
              </span>{" "}
              — {w.norm_desc}
            </p>
            {w.converted_quantity && (
              <p>
                <span className="text-amber-800">Obmiar w jm normy:&nbsp;</span>
                <span className="font-semibold">
                  {w.converted_quantity} {w.norm_unit}
                </span>
              </p>
            )}
          </div>
          <p className="leading-relaxed">{w.note}</p>
        </div>
      ))}
    </>
  );
}

// ── Podstawa zmieniona ręcznie ───────────────────────────────────────────────
// Zmiana kodu KNNR podmienia nakłady w całości; stara podstawa i liczba
// zastąpionych nakładów zostają w expansion_warnings (ślad audytowy).

function basisChangedWarnings(pos: Position): ExpansionWarning[] {
  return (pos.expansion_warnings ?? []).filter((w) => w.code === "basis_changed");
}

function BasisChangedPanel({ pos }: { pos: Position }) {
  const warnings = basisChangedWarnings(pos);
  if (warnings.length === 0) return null;
  return (
    <>
      {warnings.map((w, i) => (
        <div
          key={i}
          className="mb-3 border border-teal-300 bg-teal-50 p-2 text-[11px] text-teal-900"
        >
          <span className="font-semibold">Podstawa zmieniona ręcznie. </span>
          {w.note}
        </div>
      ))}
    </>
  );
}

function DeviceMismatchBadge() {
  return (
    <span
      className="inline-block px-1 py-px text-[9px] font-semibold border border-red-400 bg-red-50 text-red-700"
      title="Opis pozycji mówi o innym urządzeniu niż norma użyta do jej rozwinięcia — sprawdź podstawę w dokumentacji. Szczegóły po rozwinięciu pozycji."
    >
      ⚠ INNE URZĄDZENIE NIŻ W NORMIE
    </span>
  );
}

function DeviceMismatchPanel({ pos }: { pos: Position }) {
  const warnings = deviceMismatchWarnings(pos);
  if (warnings.length === 0) return null;
  return (
    <>
      {warnings.map((w, i) => (
        <div
          key={i}
          className="mb-3 border border-red-300 bg-red-50 p-3 text-[11px] text-red-900"
        >
          <p className="font-semibold text-[12px] mb-1.5">
            ⚠ Norma opisuje INNE urządzenie niż pozycja — sprawdź podstawę
          </p>
          <div className="space-y-0.5 font-mono text-[10.5px] mb-2">
            <p>
              <span className="text-red-700">Opis pozycji:&nbsp;</span>
              <span className="font-semibold">{pos.description}</span>
              {w.position_group_label ? ` → ${w.position_group_label}` : ""}
            </p>
            <p>
              <span className="text-red-700">Norma z katalogu:&nbsp;</span>
              <span className="font-semibold">{w.norm_code}</span> — {w.norm_desc}
              {w.norm_group_label ? ` → ${w.norm_group_label}` : ""}
            </p>
          </div>
          <p className="leading-relaxed">{w.note}</p>
        </div>
      ))}
    </>
  );
}

function DemontazBadge() {
  return (
    <span
      className="inline-block px-1 py-px text-[9px] font-semibold border border-orange-300 bg-orange-50 text-orange-700"
      title="Demontaż wyceniony normą montażową (analogia) — nakłady materiałowe normy zostały pominięte, liczona tylko robocizna i sprzęt. Szczegóły po rozwinięciu pozycji."
    >
      ⚠ DEMONTAŻ — bez materiałów
    </span>
  );
}

function ExpansionWarningsPanel({ pos }: { pos: Position }) {
  const warnings = demontazWarnings(pos);
  if (warnings.length === 0) return null;
  return (
    <>
      {warnings.map((w, i) => (
        <div
          key={i}
          className="mb-3 border border-orange-300 bg-orange-50 p-3 text-[11px] text-orange-900"
        >
          <p className="font-semibold text-[12px] mb-1.5">
            ⚠ Demontaż wyceniony normą montażową „przez analogię” — materiały
            pominięte
          </p>
          <div className="space-y-0.5 font-mono text-[10.5px] mb-2">
            <p>
              <span className="text-orange-700">Opis pozycji:&nbsp;</span>
              <span className="font-semibold">{pos.description}</span>
            </p>
            <p>
              <span className="text-orange-700">Norma z katalogu:&nbsp;</span>
              <span className="font-semibold">{w.norm_code}</span> — {w.norm_desc}
            </p>
          </div>
          <p className="mb-2 leading-relaxed">{w.note}</p>
          {w.stripped.length > 0 && (
            <table className="w-auto min-w-[50%]">
              <thead>
                <tr className="text-orange-700 border-b border-orange-200 text-left">
                  <th className="font-medium pb-1 pr-4">
                    Pominięty nakład materiałowy
                  </th>
                  <th className="font-medium pb-1 text-right">
                    Ilość wg normy montażu
                  </th>
                </tr>
              </thead>
              <tbody>
                {w.stripped.map((s, j) => (
                  <tr key={j} className="line-through decoration-orange-400">
                    <td className="py-0.5 pr-4">
                      <KindTag kind={s.kind} />
                      {s.description}
                      {s.is_percent_aux ? " (%)" : ""}
                    </td>
                    <td className="py-0.5 text-right tabular-nums whitespace-nowrap">
                      {money(s.norm_total)} {s.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="mt-2 text-orange-700">
            Robocizna i sprzęt poniżej policzone zgodnie z normą (bez zmian).
            Jeśli zakres robót obejmuje jednak ponowny montaż lub zakup
            materiałów — dodaj brakujący nakład ręcznie albo zgłoś uwagę.
          </p>
        </div>
      ))}
    </>
  );
}

// Save a manually-researched price to „Nasza baza cen” and re-match the input to
// the saved entry, so the next valuation finds it automatically.
function SaveToBasePanel({
  inp,
  jobId,
  onClose,
}: {
  inp: PositionInput;
  jobId: string;
  onClose: () => void;
}) {
  const { create } = useCustomPriceMutations();
  const matchInput = useMatchInput(jobId);
  const [desc, setDesc] = useState(inp.description);
  const [unit, setUnit] = useState(inp.unit);
  const [avg, setAvg] = useState(
    inp.manual_price ?? inp.ai_price ?? inp.unit_price.avg ?? "",
  );
  const [min, setMin] = useState(inp.ai_price_min ?? "");
  const [max, setMax] = useState(inp.ai_price_max ?? "");
  const [note, setNote] = useState("");
  const num = (s: string) => {
    const t = s.trim().replace(",", ".").replace(/\s/g, "");
    return t || null;
  };
  const pending = create.isPending || matchInput.isPending;
  const inputCls =
    "border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-0.5";
  return (
    <div className="mt-1 border border-emerald-300 bg-emerald-50/50 p-2 space-y-1.5">
      <div className="text-[10px] font-medium text-emerald-800">
        Zapisz do naszej bazy cen (użyta automatycznie w kolejnych wycenach)
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="nazwa pozycji"
          className={`${inputCls} flex-1 min-w-[200px]`}
        />
        <input
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          placeholder="jm"
          className={`${inputCls} w-14`}
        />
        <input
          value={avg}
          onChange={(e) => setAvg(e.target.value)}
          placeholder="cena śr."
          className={`${inputCls} w-20`}
        />
        <input
          value={min}
          onChange={(e) => setMin(e.target.value)}
          placeholder="min"
          className={`${inputCls} w-16`}
        />
        <input
          value={max}
          onChange={(e) => setMax(e.target.value)}
          placeholder="maks"
          className={`${inputCls} w-16`}
        />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="notatka (skąd cena)…"
          className={`${inputCls} flex-1 min-w-[200px]`}
        />
        <button
          className="text-[11px] text-emerald-700 font-medium hover:underline disabled:opacity-50"
          disabled={pending || !desc.trim() || !num(avg)}
          onClick={() =>
            create.mutate(
              {
                kind: inp.kind as "material" | "equipment",
                description: desc.trim(),
                unit: unit.trim(),
                price_avg: num(avg),
                price_min: num(min),
                price_max: num(max),
                note: note.trim(),
                source_url: inp.ai_price_sources?.[0]?.url ?? "",
              },
              {
                onSuccess: (entry) => {
                  matchInput.mutate({ inputId: inp.id, entryId: entry.id });
                  onClose();
                },
              },
            )
          }
        >
          {pending ? "zapisuję…" : "zapisz i dopasuj"}
        </button>
        <button
          className="text-[11px] text-[var(--color-text-muted)] hover:underline"
          onClick={onClose}
        >
          anuluj
        </button>
      </div>
      {create.isError && (
        <div className="text-[10px] text-red-600">
          Nie udało się zapisać — sprawdź nazwę i cenę średnią.
        </div>
      )}
    </div>
  );
}

// ── Komentarze kosztorysanta ─────────────────────────────────────────────────
// Wolny tekst przy pozycji / nakładzie — „cenę zaniżyłem świadomie”, „wykreśliłem
// zakres X”. Wyświetlany zawsze obok kwot (nie chowany w szczegółach), bo to
// informacja dla czytającego wycenę, nie notatka techniczna.

function CommentBubble({
  comment,
  canEdit,
  pending,
  onSave,
  className = "",
}: {
  comment: string;
  canEdit: boolean;
  pending?: boolean;
  onSave: (text: string) => void;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(comment);
  if (!comment && !editing) return null;
  if (!editing)
    return (
      <div
        className={`border-l-2 border-violet-400 bg-violet-50 pl-2 pr-2 py-1 text-[11px] text-violet-900 ${className}`}
      >
        💬 {comment}
        {canEdit && (
          <>
            <button
              className="ml-2 text-[var(--color-primary)] hover:underline"
              onClick={() => {
                setText(comment);
                setEditing(true);
              }}
            >
              edytuj
            </button>
            <button
              className="ml-1.5 text-[var(--color-text-muted)] hover:underline"
              disabled={pending}
              onClick={() => onSave("")}
            >
              usuń
            </button>
          </>
        )}
      </div>
    );
  return (
    <div className={`flex items-start gap-1.5 ${className}`}>
      <textarea
        autoFocus
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="np. cenę zaniżyłem świadomie o ~15% — konkurencja z Torunia; zakres X wykreśliłem"
        className="flex-1 border border-violet-300 bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-1"
      />
      <button
        className="text-[11px] text-[var(--color-primary)] hover:underline"
        disabled={pending}
        onClick={() => {
          onSave(text.trim());
          setEditing(false);
        }}
      >
        zapisz
      </button>
      <button
        className="text-[11px] text-[var(--color-text-muted)] hover:underline"
        onClick={() => setEditing(false)}
      >
        anuluj
      </button>
    </div>
  );
}

/** Pusty edytor komentarza (pozycja/nakład jeszcze bez komentarza). */
function NewCommentEditor({
  pending,
  onSave,
  onCancel,
}: {
  pending?: boolean;
  onSave: (text: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState("");
  return (
    <div className="flex items-start gap-1.5">
      <textarea
        autoFocus
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="np. cenę zaniżyłem świadomie o ~15% — walczymy ceną; zakres X wykreśliłem, robi to inwestor"
        className="flex-1 border border-violet-300 bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-1"
      />
      <button
        className="text-[11px] text-[var(--color-primary)] hover:underline"
        disabled={pending || !text.trim()}
        onClick={() => onSave(text.trim())}
      >
        zapisz
      </button>
      <button
        className="text-[11px] text-[var(--color-text-muted)] hover:underline"
        onClick={onCancel}
      >
        anuluj
      </button>
    </div>
  );
}

/** Przycisk „+ komentarz” — otwiera pustą bańkę komentarza (stan trzyma rodzic). */
function AddCommentButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      className="text-[11px] text-violet-700 hover:underline whitespace-nowrap"
      title="Dodaj komentarz widoczny przy kwotach (np. dlaczego cena jest zaniżona)"
      onClick={onClick}
    >
      💬 komentarz
    </button>
  );
}

// ── Pozycje dopisywane ręcznie ───────────────────────────────────────────────

const MANUAL_MODE_OPTIONS: { key: ManualPricingMode; label: string }[] = [
  { key: "complex", label: "Kompleksowa — kwota końcowa" },
  { key: "markup", label: "Dolicz Kp + Z" },
  { key: "inputs", label: "Rozbij na nakłady R/M/S" },
];

/** Skrót trybu wyceny na czerwonej plakietce pozycji dopisanej ręcznie. */
const MANUAL_MODE_TAG: Record<ManualPricingMode | "", string> = {
  markup: "+Kp+Z",
  complex: "kompleksowa",
  inputs: "z nakładów R/M/S",
  "": "kompleksowa",
};

const MANUAL_MODE_TITLE: Record<ManualPricingMode | "", string> = {
  markup:
    "Pozycja dopisana ręcznie — do kwoty doliczane są koszty pośrednie (Kp) i zysk (Z)",
  complex:
    "Pozycja dopisana ręcznie — kwota kompleksowa, narzutów nie dolicza się",
  inputs:
    "Pozycja dopisana ręcznie — wyceniana z dopisanych nakładów R/M/S (Kp i Z od robocizny i sprzętu, Kz od materiałów); materiały wchodzą do zestawienia materiałów",
  "": "Pozycja dopisana ręcznie — kwota kompleksowa, narzutów nie dolicza się",
};

/** Rodzaj nakładu dopisywanego ręcznie — od niego zależą narzuty i zestawienia. */
const MANUAL_INPUT_KINDS: {
  key: "material" | "labor" | "equipment";
  label: string;
}[] = [
  { key: "material", label: "M — materiał (zestawienie materiałów, narzut Kz)" },
  { key: "labor", label: "R — robocizna (narzuty Kp + Z)" },
  { key: "equipment", label: "S — sprzęt (narzuty Kp + Z, zestawienie sprzętu)" },
];

/** Dopisanie nakładu R/M/S pod pozycją dodaną ręcznie — „szafka” osobno od
 *  „montażu szafki”, żeby materiał trafił do zestawienia materiałów, a robocizna
 *  złapała Kp i Z. */
function ManualInputForm({
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  pending?: boolean;
  error?: string;
  onSubmit: (d: {
    kind: "material" | "labor" | "equipment";
    description: string;
    unit: string;
    norm_total: string;
    price: string;
  }) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<"material" | "labor" | "equipment">("material");
  const [description, setDescription] = useState("");
  const [unit, setUnit] = useState("szt.");
  const [norm, setNorm] = useState("1");
  const [price, setPrice] = useState("");
  const inputCls =
    "border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-1";
  const num = (v: string) => Number(v.trim().replace(",", ".").replace(/\s/g, ""));
  const valid = description.trim() !== "" && num(norm) > 0;
  return (
    <div className="mt-2 border border-red-300 bg-red-50/40 p-2 space-y-2">
      <div className="text-[11px] font-semibold text-red-700">
        Dopisz nakład do pozycji ręcznej
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)]">
          Rodzaj
          <select
            value={kind}
            onChange={(e) => {
              const k = e.target.value as "material" | "labor" | "equipment";
              setKind(k);
              setUnit(k === "labor" ? "r-g" : k === "equipment" ? "m-g" : "szt.");
            }}
            className={inputCls}
          >
            {MANUAL_INPUT_KINDS.map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)] flex-1 min-w-[200px]">
          Nazwa nakładu
          <input
            autoFocus
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="np. Szafka pomiarowa P1-Rs/LZV/LZR/F"
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)]">
          jm
          <input
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className={`${inputCls} w-16`}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)]">
          Ilość
          <input
            value={norm}
            onChange={(e) => setNorm(e.target.value)}
            className={`${inputCls} w-20 text-right`}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)]">
          Cena jedn. [zł]
          <input
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder={kind === "labor" ? "puste = stawka r-g" : "np. 2500"}
            className={`${inputCls} w-28 text-right`}
          />
        </label>
      </div>
      <p className="text-[10px] text-[var(--color-text-muted)]">
        {kind === "labor"
          ? "Robocizna bez wpisanej ceny liczy się stawką roboczogodziny z narzutów tej wyceny."
          : "Bez ceny nakład zostanie bez wyceny — cenę można potem dopasować z cennika albo wycenić AI."}
      </p>
      {error && <p className="text-[11px] text-red-600">{error}</p>}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!valid}
          loading={pending}
          onClick={() =>
            onSubmit({
              kind,
              description: description.trim(),
              unit: unit.trim(),
              norm_total: norm.trim(),
              price: price.trim(),
            })
          }
        >
          Dodaj nakład
        </Button>
        <button
          className="text-[11px] text-[var(--color-text-secondary)] underline"
          onClick={onCancel}
        >
          Anuluj
        </button>
      </div>
    </div>
  );
}

interface ManualDraft {
  description: string;
  unit: string;
  quantity: string;
  price: string;
  pricing_mode: ManualPricingMode;
  category: PositionCategory;
  comment: string;
  /** Ustawiane, gdy pozycję wzięto z „Naszej bazy cen” — zostaje jako źródło ceny. */
  entry_id?: string;
}

const CUSTOM_KIND_LABEL: Record<CustomPriceEntry["kind"], string> = {
  work_unit: "pozycja główna",
  material: "materiał",
  equipment: "sprzęt",
};

/** Wybór pozycji z „Naszej bazy cen” — pozycje główne (nadzór archeologiczny,
 *  obsługa geodezyjna…) najpierw, bo po to głównie się tu wchodzi. */
function CustomBasePicker({
  onPick,
  onClose,
}: {
  onPick: (e: CustomPriceEntry) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const base = useCustomPrices(q, "");
  const items = [...(base.data ?? [])].sort(
    (a, b) => Number(b.kind === "work_unit") - Number(a.kind === "work_unit"),
  );
  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 space-y-1.5">
      <div className="flex items-center gap-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Szukaj w naszej bazie cen (np. archeolog, geodezja)…"
          className="flex-1 border border-[var(--color-border)] bg-[var(--color-bg-base)] text-[11px] px-1.5 py-1"
        />
        <button
          className="text-[11px] text-[var(--color-text-secondary)] underline"
          onClick={onClose}
        >
          Anuluj
        </button>
      </div>
      <div className="max-h-52 overflow-y-auto divide-y divide-[var(--color-border)]">
        {base.isLoading && (
          <p className="text-[11px] text-[var(--color-text-muted)] py-1">Wczytywanie…</p>
        )}
        {!base.isLoading && items.length === 0 && (
          <p className="text-[11px] text-[var(--color-text-muted)] py-1">
            Nic nie znaleziono. Pozycje główne (nadzory, geodezja) dodasz w zakładce
            „Nasza baza cen”.
          </p>
        )}
        {items.map((e) => (
          <button
            key={e.id}
            className="w-full text-left py-1 hover:bg-[var(--color-bg-base)]"
            onClick={() => onPick(e)}
          >
            <span className="text-[11px] text-[var(--color-text-primary)]">
              {e.description}
            </span>
            <span className="ml-2 text-[10px] text-[var(--color-text-muted)] font-mono">
              {e.code} · {CUSTOM_KIND_LABEL[e.kind]} · {money(e.price_avg)} zł
              {e.unit ? `/${e.unit}` : ""}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function emptyDraft(pos?: Position): ManualDraft {
  return {
    description: pos?.description ?? "",
    unit: pos?.unit ?? "kpl.",
    quantity: pos?.quantity != null ? String(Number(pos.quantity)) : "1",
    price: pos?.manual_price ?? "",
    // Nowa pozycja startuje Z NARZUTAMI: dopisujemy najczęściej robociznę
    // („doliczenie robocizny do poz. 24”), a kompleksowa oddawała od tej kwoty
    // Kp i Z po cichu. Kwotę końcową od dostawcy przestawia się jednym kliknięciem.
    pricing_mode: (pos?.manual_pricing_mode || "markup") as ManualPricingMode,
    category: pos?.category ?? "inne",
    comment: pos?.comment ?? "",
  };
}

function ManualPositionForm({
  pos,
  method,
  kpPct,
  zPct,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  pos?: Position;
  /** Metoda kosztorysu — podział R/M/S istnieje tylko w szczegółowym. */
  method: string;
  kpPct: string;
  zPct: string;
  pending?: boolean;
  error?: string;
  onSubmit: (d: ManualDraft) => void;
  onCancel: () => void;
}) {
  const [d, setD] = useState<ManualDraft>(() => emptyDraft(pos));
  const set = <K extends keyof ManualDraft>(k: K, v: ManualDraft[K]) =>
    setD((prev) => ({ ...prev, [k]: v }));
  // Wybór z „Naszej bazy cen” — tylko przy dopisywaniu nowej pozycji; przy
  // poprawianiu istniejącej edytuje się już same kwoty.
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<CustomPriceEntry | null>(null);
  const inputCls =
    "border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-1";
  const num = (s: string) => Number(s.trim().replace(",", ".").replace(/\s/g, ""));
  const price = num(d.price);
  const qty = num(d.quantity);
  const fromInputs = d.pricing_mode === "inputs";
  const amount = Number.isFinite(price) && Number.isFinite(qty) ? price * qty : NaN;
  const kp = d.pricing_mode === "markup" ? (amount * Number(kpPct || 0)) / 100 : 0;
  const z = d.pricing_mode === "markup" ? ((amount + kp) * Number(zPct || 0)) / 100 : 0;
  // W trybie „z nakładów” pozycja nie ma ceny jednostkowej — wartość policzy się
  // z dopisanych nakładów R/M/S, więc cena nie jest wymagana do zapisu.
  const valid =
    d.description.trim() !== "" &&
    qty > 0 &&
    (fromInputs || Number.isFinite(price));
  const modeOptions = MANUAL_MODE_OPTIONS.filter(
    (m) => m.key !== "inputs" || method !== "simplified",
  );
  return (
    <div className="border border-[var(--color-primary)] bg-[var(--color-bg-base)] p-3 space-y-2">
      <div className="text-[11px] font-semibold text-[var(--color-text-primary)]">
        {pos ? "Popraw pozycję dopisaną ręcznie" : "Dopisz pozycję do kosztorysu"}
      </div>
      {!pos &&
        (picking ? (
          <CustomBasePicker
            onClose={() => setPicking(false)}
            onPick={(e) => {
              setPicked(e);
              setPicking(false);
              setD((prev) => ({
                ...prev,
                description: e.description,
                unit: e.unit || prev.unit,
                price: e.price_avg ?? prev.price,
                entry_id: e.id,
              }));
            }}
          />
        ) : picked ? (
          <p className="text-[11px] text-[var(--color-text-secondary)]">
            Z naszej bazy cen:{" "}
            <span className="font-mono text-[var(--color-text-primary)]">
              {picked.code}
            </span>{" "}
            · {picked.description}{" "}
            <button
              className="ml-1 underline text-[var(--color-text-muted)]"
              onClick={() => {
                setPicked(null);
                set("entry_id", undefined);
              }}
            >
              odepnij
            </button>
          </p>
        ) : (
          <p className="text-[11px] text-[var(--color-text-secondary)]">
            <button
              className="text-[var(--color-primary)] font-medium hover:underline"
              onClick={() => setPicking(true)}
            >
              Weź z naszej bazy cen…
            </button>{" "}
            <span className="text-[var(--color-text-muted)]">
              (nadzory, geodezja i inne pozycje główne) — albo wpisz wszystko z ręki
              poniżej.
            </span>
          </p>
        ))}
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)] flex-1 min-w-[240px]">
          Opis pozycji
          <input
            autoFocus
            value={d.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="np. Organizacja ruchu — projekt czasowej organizacji"
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)]">
          jm
          <input
            value={d.unit}
            onChange={(e) => set("unit", e.target.value)}
            className={`${inputCls} w-16`}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)]">
          Obmiar
          <input
            value={d.quantity}
            onChange={(e) => set("quantity", e.target.value)}
            className={`${inputCls} w-20 text-right`}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)]">
          Cena jedn. [zł]
          <input
            value={fromInputs ? "" : d.price}
            disabled={fromInputs}
            onChange={(e) => set("price", e.target.value)}
            placeholder={fromInputs ? "z nakładów" : "np. 2500 lub -800"}
            className={`${inputCls} w-28 text-right disabled:opacity-50`}
          />
        </label>
        <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)]">
          Kategoria
          <select
            value={d.category}
            onChange={(e) => set("category", e.target.value as PositionCategory)}
            className={inputCls}
          >
            {CATEGORY_ORDER.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {modeOptions.map((m) => (
          <label
            key={m.key}
            className="flex items-center gap-1 text-[11px] text-[var(--color-text-primary)]"
          >
            <input
              type="radio"
              checked={d.pricing_mode === m.key}
              onChange={() => set("pricing_mode", m.key)}
            />
            {m.label}
            {m.key === "markup" && (
              <span className="text-[var(--color-text-muted)]">
                (Kp {kpPct || "?"}% + Z {zPct || "?"}%)
              </span>
            )}
            {m.key === "inputs" && (
              <span className="text-[var(--color-text-muted)]">
                (materiał osobno od montażu — narzuty jak w kosztorysie)
              </span>
            )}
          </label>
        ))}
      </div>
      <label className="flex flex-col gap-0.5 text-[10px] text-[var(--color-text-secondary)]">
        Komentarz (opcjonalnie — widoczny przy kwocie i w PDF)
        <input
          value={d.comment}
          onChange={(e) => set("comment", e.target.value)}
          placeholder="np. dopisane, bo brakowało w przedmiarze inwestora"
          className={inputCls}
        />
      </label>
      {fromInputs ? (
        <p className="text-[11px] text-[var(--color-text-secondary)]">
          Wartość policzy się z nakładów R/M/S, które dopiszesz pod pozycją
          („Szczegóły” → „dodaj nakład”): materiał wchodzi do zestawienia materiałów
          i zbiera Kz, robocizna i sprzęt zbierają Kp i Z — dokładnie jak pozycja
          z kosztorysu.
        </p>
      ) : (
      <p className="text-[11px] text-[var(--color-text-secondary)] tabular-nums">
        {Number.isFinite(amount) ? (
          <>
            Wartość pozycji:{" "}
            <span className="font-semibold text-[var(--color-text-primary)]">
              {money(String(amount))} zł
            </span>
            {d.pricing_mode === "markup" && (
              <>
                {" "}
                + Kp {money(String(kp))} + Z {money(String(z))} ={" "}
                <span className="font-semibold text-[var(--color-primary)]">
                  {money(String(amount + kp + z))} zł
                </span>
              </>
            )}
          </>
        ) : (
          "Podaj obmiar i cenę jednostkową."
        )}
      </p>
      )}
      {!fromInputs && (
        <p className="text-[10px] text-[var(--color-text-muted)]">
          Cena ujemna (np. <span className="font-mono">-800</span>) odejmuje od wyceny
          — tak wpisz rabat albo zakres, który wykreślasz.
        </p>
      )}
      {error && <p className="text-[11px] text-red-600">{error}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!valid} loading={pending} onClick={() => onSubmit(d)}>
          {pos ? "Zapisz zmiany" : "Dodaj pozycję"}
        </Button>
        <button
          className="text-[11px] text-[var(--color-text-secondary)] underline"
          onClick={onCancel}
        >
          Anuluj
        </button>
      </div>
    </div>
  );
}

function ProofLine({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex justify-between gap-6">
      <dt className="text-[var(--color-text-secondary)]">{label}</dt>
      <dd className="tabular-nums text-[var(--color-text-primary)]">
        {money(value)}
      </dd>
    </div>
  );
}

function PositionRow({
  pos,
  method,
  ctx,
  open,
  onToggle,
}: {
  pos: Position;
  method: string;
  ctx: MatchCtx;
  open: boolean;
  onToggle: () => void;
}) {
  // picking: which input (id) or the position itself ("__pos__") is being matched.
  const [picking, setPicking] = useState<string | null>(null);
  // pricing: which input (id) is having a manual price typed; priceText holds it.
  const [pricing, setPricing] = useState<string | null>(null);
  const [priceText, setPriceText] = useState("");
  // savingBase: which input (id) is being saved to „Nasza baza cen”.
  const [savingBase, setSavingBase] = useState<string | null>(null);
  // commenting: pozycja („__pos__”) lub nakład (id) z otwartą pustą bańką komentarza.
  const [commenting, setCommenting] = useState<string | null>(null);
  const [editingManual, setEditingManual] = useState(false);
  // renaming: pozycja („__pos__”) lub nakład (id) z otwartym edytorem nazwy;
  // nameText trzyma wpisywany tekst.
  const [renaming, setRenaming] = useState<string | null>(null);
  const [nameText, setNameText] = useState("");
  // editingQty: otwarty edytor ręcznej zmiany obmiaru; qtyText trzyma wpisywaną wartość.
  const [editingQty, setEditingQty] = useState(false);
  const [qtyText, setQtyText] = useState("");
  // editingNorm: nakład (id) z otwartym edytorem ILOŚCI („Nakłady”); normText — wartość.
  const [editingNorm, setEditingNorm] = useState<string | null>(null);
  const [normText, setNormText] = useState("");
  // addingInput: otwarty formularz dopisania nakładu R/M/S do pozycji ręcznej.
  const [addingInput, setAddingInput] = useState(false);
  const posComment = useSetPositionComment(ctx.jobId);
  const posDescription = useSetPositionDescription(ctx.jobId);
  const inputDescription = useSetInputDescription(ctx.jobId);
  const posManualPrice = useSetPositionManualPrice(ctx.jobId);
  const posQuantity = useSetPositionQuantity(ctx.jobId);
  const posInvestor = useSetPositionInvestorSupplied(ctx.jobId);
  const inputComment = useSetInputComment(ctx.jobId);
  const manual = useManualPositions(ctx.jobId);
  const matchInput = useMatchInput(ctx.jobId);
  const matchPosition = useMatchPosition(ctx.jobId);
  const investor = useSetInvestorSupplied(ctx.jobId);
  const manualPrice = useSetManualPrice(ctx.jobId);
  const setCategory = useSetPositionCategory(ctx.jobId);
  const aiPriceInput = useAiPriceInput(ctx.jobId);
  const aiPricePosition = useAiPricePosition(ctx.jobId);
  // Zmiana podstawy (kodu KNNR) + kategoria gruntu + narzuty ryczałtu + wykreślenia.
  const [editingBasis, setEditingBasis] = useState(false);
  const [basisText, setBasisText] = useState("");
  const [basisError, setBasisError] = useState("");
  const [basisVariants, setBasisVariants] = useState<NormVariant[]>([]);
  const setBasis = useSetPositionBasis(ctx.jobId);
  const setSoil = useSetPositionSoilCategory(ctx.jobId);
  const setWholeMarkup = useSetPositionWholeMarkup(ctx.jobId);
  const setExcluded = useSetInputExcluded(ctx.jobId);
  // Przeniesienie nakładu między R, M i S — od rodzaju zależą narzuty (Kp i Z
  // liczą się od robocizny i sprzętu, Kz od materiałów).
  const setInputKind = useSetInputKind(ctx.jobId);
  // Ilość nakładu (kolumna „Nakłady”) i nakłady dopisywane ręcznie pod pozycją ręczną.
  const setInputNorm = useSetInputNorm(ctx.jobId);
  const manualInputs = useManualInputs(ctx.jobId);
  // Podpowiedzi z katalogu norm dla wpisywanej podstawy (kod lub fragment opisu)
  // — zamiast wpisywania kodu w ciemno i poprawiania po błędzie.
  const normHits = useNormSearch(editingBasis ? basisText : "");

  const saveBasis = (basis: string) => {
    setBasisError("");
    setBasisVariants([]);
    setBasis.mutate(
      { positionId: pos.id, basis },
      {
        onSuccess: () => setEditingBasis(false),
        onError: (e: unknown) => {
          const errs = (
            e as {
              response?: {
                data?: { errors?: { detail?: string; variants?: NormVariant[] } };
              };
            }
          )?.response?.data?.errors;
          setBasisError(errs?.detail || "Nie udało się zmienić podstawy.");
          setBasisVariants(errs?.variants ?? []);
        },
      },
    );
  };
  const pickKind =
    picking && picking !== "__pos__"
      ? (pos.inputs.find((i) => i.id === picking)?.kind ?? "material")
      : "work_unit";

  // Wycena CAŁEJ pozycji (bez nakładów R/M/S albo metoda uproszczona):
  // „inwestorska” i „wpisz ręcznie” — lustrzane odbicie kontrolek nakładu.
  const positionPriceButtons = ctx.canEdit && !pos.investor_supplied && (
    <>
      <button
        className="ml-2 text-blue-600 hover:underline"
        title="Oznacz całą pozycję jako dostawę inwestorską (0 zł)"
        onClick={() => posInvestor.mutate({ positionId: pos.id, value: true })}
      >
        inwestorska
      </button>
      <button
        className="ml-2 text-[var(--color-text-secondary)] hover:underline"
        title="Wpisz cenę jednostkową pozycji ręcznie (nadpisuje cennik i wycenę AI)"
        onClick={() => {
          setPriceText(pos.manual_price ?? "");
          setPricing((p) => (p === "__pos__" ? null : "__pos__"));
        }}
      >
        wpisz ręcznie
      </button>
    </>
  );
  const positionPriceStatus = (
    <>
      {pos.investor_supplied && (
        <p className="mt-1 text-[11px] font-mono text-blue-600">
          dostawa inwestorska — pozycja wyceniana jawnie na 0 zł
          {ctx.canEdit && (
            <button
              className="ml-1.5 text-[var(--color-primary)] hover:underline"
              onClick={() =>
                posInvestor.mutate({ positionId: pos.id, value: false })
              }
            >
              cofnij
            </button>
          )}
        </p>
      )}
      {!pos.investor_supplied && pos.manual_price != null && (
        <p className="mt-1 text-[11px] font-mono text-red-600">
          cena ręczna: {money(pos.manual_price)} zł × obmiar{" "}
          {money(pos.quantity)} {pos.unit}
          {ctx.canEdit && (
            <button
              className="ml-1.5 text-[var(--color-primary)] hover:underline"
              onClick={() =>
                posManualPrice.mutate({ positionId: pos.id, price: null })
              }
            >
              usuń
            </button>
          )}
        </p>
      )}
      {pricing === "__pos__" && (
        <div className="mt-1 flex items-center gap-1">
          <input
            autoFocus
            value={priceText}
            onChange={(e) => setPriceText(e.target.value)}
            placeholder="cena jedn. zł"
            className="w-24 border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-0.5"
          />
          <button
            className="text-[11px] text-[var(--color-primary)] hover:underline"
            onClick={() => {
              posManualPrice.mutate({
                positionId: pos.id,
                price: priceText.trim() || null,
              });
              setPricing(null);
            }}
          >
            zapisz
          </button>
          <button
            className="text-[11px] text-[var(--color-text-muted)] hover:underline"
            onClick={() => setPricing(null)}
          >
            anuluj
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      <tr className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-base)]">
        <td className="px-2 py-1.5 text-xs text-[var(--color-text-muted)] tabular-nums">
          {pos.ordinal_label || pos.ordinal}
        </td>
        <td className="px-2 py-1.5 text-xs font-mono whitespace-nowrap">
          {pos.basis_raw || "—"}
          {ctx.canEdit && !pos.is_manual && (
            <button
              className="ml-1 text-[10px] text-[var(--color-primary)] hover:underline"
              title="Zmień podstawę (kod KNNR/KNR) — nakłady R/M/S zostaną zaciągnięte na nowo z katalogu norm i przeliczone"
              onClick={() => {
                setBasisText(pos.basis_raw);
                setBasisError("");
                setBasisVariants([]);
                setEditingBasis((v) => !v);
              }}
            >
              zmień
            </button>
          )}
        </td>
        <td className="px-2 py-1.5 text-xs">
          {renaming === "__pos__" ? (
            <span className="flex items-start gap-1.5">
              <textarea
                autoFocus
                rows={2}
                value={nameText}
                onChange={(e) => setNameText(e.target.value)}
                className="flex-1 border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-xs px-1.5 py-1"
              />
              <button
                className="text-[11px] text-[var(--color-primary)] hover:underline"
                disabled={posDescription.isPending || !nameText.trim()}
                onClick={() =>
                  posDescription.mutate(
                    { positionId: pos.id, description: nameText.trim() },
                    { onSuccess: () => setRenaming(null) },
                  )
                }
              >
                zapisz
              </button>
              <button
                className="text-[11px] text-[var(--color-text-muted)] hover:underline"
                onClick={() => setRenaming(null)}
              >
                anuluj
              </button>
            </span>
          ) : (
            pos.description
          )}
          <span className="mt-0.5 flex items-center gap-1 flex-wrap">
            {ctx.canEdit && !pos.is_manual && renaming !== "__pos__" && (
              <button
                className="text-[10px] text-[var(--color-primary)] hover:underline"
                title="Zmień nazwę pozycji (opis z dokumentu zostanie zachowany)"
                onClick={() => {
                  setNameText(pos.description);
                  setRenaming("__pos__");
                }}
              >
                zmień nazwę
              </button>
            )}
            <span
              className={`inline-block px-1 py-px text-[9px] font-medium ${CATEGORY_STYLE[pos.category].tag}`}
            >
              {CATEGORY_LABELS[pos.category]}
            </span>
            {pos.is_manual && (
              <ManualBadge
                label={`RĘCZNA · ${MANUAL_MODE_TAG[pos.manual_pricing_mode]}`}
                title={MANUAL_MODE_TITLE[pos.manual_pricing_mode]}
              />
            )}
            {pos.description_original && (
              <ManualBadge
                label="NAZWA ZMIENIONA"
                title={`Nazwa zmieniona ręcznie — opis z dokumentu: ${pos.description_original}`}
              />
            )}
            {ctx.canEdit && pos.description_original && (
              <button
                className="text-[9px] text-[var(--color-text-muted)] hover:underline"
                disabled={posDescription.isPending}
                title="Przywróć opis z dokumentu"
                onClick={() =>
                  posDescription.mutate({ positionId: pos.id, description: "" })
                }
              >
                przywróć
              </button>
            )}
            {demontazWarnings(pos).length > 0 && <DemontazBadge />}
            {deviceMismatchWarnings(pos).length > 0 && <DeviceMismatchBadge />}
            {unitMismatchWarnings(pos).length > 0 && (
              <UnitMismatchBadge w={unitMismatchWarnings(pos)[0]} />
            )}
            {unitScaleWarnings(pos).length > 0 && (
              <UnitScaleBadge w={unitScaleWarnings(pos)[0]} />
            )}
            {basisChangedWarnings(pos).length > 0 && (
              <span
                className="inline-block px-1 py-px text-[9px] font-medium border border-teal-300 bg-teal-50 text-teal-700"
                title={basisChangedWarnings(pos)[0].note}
              >
                PODSTAWA ZMIENIONA
              </span>
            )}
            {pos.effective_soil_category &&
              !pos.is_manual &&
              (ctx.canEdit && pos.inputs.length > 0 ? (
                <select
                  value={pos.effective_soil_category}
                  disabled={setSoil.isPending}
                  title="Kategoria gruntu — zmiana podmienia wariant KNNR: nowe nakłady, inny sprzęt, przeliczona wartość"
                  onChange={(e) =>
                    setSoil.mutate(
                      { positionId: pos.id, category: e.target.value },
                      {
                        onError: (er: unknown) => {
                          const detail = (
                            er as {
                              response?: {
                                data?: { errors?: { detail?: string } };
                              };
                            }
                          )?.response?.data?.errors?.detail;
                          window.alert(
                            detail || "Nie udało się zmienić kategorii gruntu.",
                          );
                        },
                      },
                    )
                  }
                  className="text-[9px] bg-transparent text-[var(--color-text-muted)] hover:text-[var(--color-primary)] cursor-pointer border-0 p-0"
                >
                  {["I-II", "III", "IV"]
                    .concat(
                      ["I-II", "III", "IV"].includes(pos.effective_soil_category)
                        ? []
                        : [pos.effective_soil_category],
                    )
                    .map((c) => (
                      <option key={c} value={c}>
                        grunt kat. {c}
                      </option>
                    ))}
                </select>
              ) : (
                <span className="inline-block px-1 py-px text-[9px] font-medium border border-stone-300 bg-stone-50 text-stone-700">
                  grunt kat. {pos.effective_soil_category}
                </span>
              ))}
            {ctx.canEdit && (
              <select
                value={pos.category}
                title="Zmień kategorię kosztową"
                onChange={(e) =>
                  setCategory.mutate({
                    positionId: pos.id,
                    category: e.target.value,
                  })
                }
                className="text-[9px] bg-transparent text-[var(--color-text-muted)] hover:text-[var(--color-primary)] cursor-pointer border-0 p-0"
              >
                {CATEGORY_ORDER.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            )}
          </span>
        </td>
        <td className="px-2 py-1.5 text-xs text-right whitespace-nowrap tabular-nums">
          {editingQty ? (
            <span className="flex items-center justify-end gap-1">
              <input
                autoFocus
                value={qtyText}
                onChange={(e) => setQtyText(e.target.value)}
                placeholder="obmiar"
                className="w-20 border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-0.5 text-right tabular-nums"
              />
              <button
                className="text-[11px] text-[var(--color-primary)] hover:underline"
                disabled={posQuantity.isPending || !qtyText.trim()}
                onClick={() =>
                  posQuantity.mutate(
                    { positionId: pos.id, quantity: qtyText.trim() },
                    {
                      onSuccess: () => setEditingQty(false),
                      onError: (er: unknown) => {
                        const detail = (
                          er as {
                            response?: {
                              data?: { errors?: { detail?: string } };
                            };
                          }
                        )?.response?.data?.errors?.detail;
                        window.alert(
                          detail || "Nie udało się zmienić obmiaru.",
                        );
                      },
                    },
                  )
                }
              >
                zapisz
              </button>
              <button
                className="text-[11px] text-[var(--color-text-muted)] hover:underline"
                onClick={() => setEditingQty(false)}
              >
                anuluj
              </button>
            </span>
          ) : (
            <>
              <span
                className={
                  pos.quantity_original != null
                    ? "font-semibold text-red-600"
                    : undefined
                }
              >
                {money(pos.quantity)} {pos.unit}
              </span>
              {ctx.canEdit && !pos.is_manual && (
                <button
                  className="ml-1 text-[10px] text-[var(--color-primary)] hover:underline"
                  title="Zmień obmiar (długość/ilość) ręcznie — nakłady R/M/S zostaną przeskalowane, a wycena przeliczona"
                  onClick={() => {
                    setQtyText(String(pos.quantity).replace(".", ","));
                    setEditingQty(true);
                  }}
                >
                  zmień
                </button>
              )}
              {pos.quantity_original != null && (
                <div className="mt-0.5">
                  <ManualBadge
                    label="OBMIAR ZMIENIONY"
                    title={`Obmiar zmieniony ręcznie — z dokumentu: ${money(pos.quantity_original)} ${pos.unit}`}
                  />
                  {ctx.canEdit && (
                    <button
                      className="ml-1 text-[9px] text-[var(--color-text-muted)] hover:underline"
                      title={`Przywróć obmiar z dokumentu (${money(pos.quantity_original)} ${pos.unit})`}
                      disabled={posQuantity.isPending}
                      onClick={() =>
                        posQuantity.mutate({
                          positionId: pos.id,
                          quantity: null,
                        })
                      }
                    >
                      przywróć
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </td>
        {VARIANTS.map((v) => (
          <td
            key={v.key}
            className={`px-2 py-1.5 text-xs text-right whitespace-nowrap tabular-nums ${
              v.key === ctx.variant
                ? "font-semibold text-[var(--color-text-primary)]"
                : "text-[var(--color-text-secondary)]"
            }`}
          >
            {money(pos.computed[v.key]?.value)}
          </td>
        ))}
        <td className="px-2 py-1.5 text-right">
          <div className="flex flex-col items-end gap-0.5">
            <button
              className="text-[11px] text-[var(--color-primary)] hover:underline whitespace-nowrap"
              onClick={onToggle}
            >
              {open ? "Zwiń ▴" : "Szczegóły ▾"}
            </button>
            {ctx.canEdit && !pos.comment && commenting !== "__pos__" && (
              <AddCommentButton onClick={() => setCommenting("__pos__")} />
            )}
            {ctx.canEdit && pos.is_manual && (
              <span className="flex items-center gap-1.5">
                <button
                  className="text-[11px] text-[var(--color-primary)] hover:underline"
                  onClick={() => setEditingManual((e) => !e)}
                >
                  edytuj
                </button>
                <button
                  className="text-[11px] text-red-600 hover:underline disabled:opacity-50"
                  disabled={manual.remove.isPending}
                  title="Usuń tę dopisaną pozycję z wyceny"
                  onClick={() => {
                    if (
                      window.confirm(
                        `Usunąć dopisaną pozycję „${pos.description}” z wyceny?`,
                      )
                    )
                      manual.remove.mutate(pos.id);
                  }}
                >
                  usuń
                </button>
              </span>
            )}
          </div>
        </td>
      </tr>
      {/* Edytor podstawy: nowy kod KNNR → nakłady zaciągane na nowo z norm. */}
      {editingBasis && (
        <tr className="bg-[var(--color-bg-elevated)]">
          <td colSpan={8} className="px-3 py-2">
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-[var(--color-text-secondary)]">
                Nowa podstawa:
              </span>
              <input
                autoFocus
                value={basisText}
                onChange={(e) => setBasisText(e.target.value)}
                placeholder="np. KNNR 5 0701-05"
                className="w-44 border border-[var(--color-border)] bg-[var(--color-bg-surface)] font-mono text-[11px] px-1.5 py-0.5"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && basisText.trim())
                    saveBasis(basisText.trim());
                }}
              />
              <button
                className="text-[var(--color-primary)] hover:underline disabled:opacity-50"
                disabled={setBasis.isPending || !basisText.trim()}
                onClick={() => saveBasis(basisText.trim())}
              >
                {setBasis.isPending ? "przeliczam…" : "zapisz i przelicz"}
              </button>
              <button
                className="text-[var(--color-text-muted)] hover:underline"
                onClick={() => setEditingBasis(false)}
              >
                anuluj
              </button>
              <span className="text-[10px] text-[var(--color-text-muted)]">
                — nakłady R/M/S zostaną zastąpione nakładami nowej tablicy
                (stara podstawa zostaje w historii pozycji)
              </span>
            </div>
            {!basisError &&
              basisVariants.length === 0 &&
              (normHits.data ?? []).length > 0 && (
                <div className="mt-1">
                  <p className="text-[10px] text-[var(--color-text-muted)]">
                    Podpowiedzi z katalogu norm (kliknij, by wybrać i przeliczyć):
                  </p>
                  {(normHits.data ?? [])
                    .filter((h) => h.code !== pos.normalized_code)
                    .slice(0, 8)
                    .map((h) => (
                      <button
                        key={h.code}
                        className="block max-w-[480px] truncate text-left text-[10px] text-[var(--color-primary)] hover:underline"
                        title={`${h.code} — ${h.description}`}
                        onClick={() => saveBasis(h.code)}
                      >
                        <span className="font-mono">{h.code}</span> —{" "}
                        {h.description}
                        {h.soil_category && ` (grunt kat. ${h.soil_category})`}
                      </button>
                    ))}
                </div>
              )}
            {basisError && (
              <p className="mt-1 text-[11px] text-red-600">{basisError}</p>
            )}
            {basisVariants.length > 0 && (
              <div className="mt-1">
                <p className="text-[10px] text-[var(--color-text-muted)]">
                  Dostępne warianty tej tablicy (kliknij, by wybrać):
                </p>
                {basisVariants.map((v) => (
                  <button
                    key={v.code}
                    className="block text-left text-[10px] text-[var(--color-primary)] hover:underline max-w-[480px] truncate"
                    title={`${v.code} — ${v.description}`}
                    onClick={() => saveBasis(v.code)}
                  >
                    <span className="font-mono">{v.code}</span> — {v.description}
                    {v.soil_category && ` (grunt kat. ${v.soil_category})`}
                  </button>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
      {/* Komentarz kosztorysanta — zawsze przy kwotach, nie w szczegółach. */}
      {(pos.comment || commenting === "__pos__") && (
        <tr className="bg-[var(--color-bg-base)]">
          <td colSpan={8} className="px-3 py-1">
            {pos.comment ? (
              <CommentBubble
                comment={pos.comment}
                canEdit={ctx.canEdit}
                pending={posComment.isPending}
                onSave={(text) => {
                  posComment.mutate({ positionId: pos.id, comment: text });
                  setCommenting(null);
                }}
              />
            ) : (
              <NewCommentEditor
                pending={posComment.isPending}
                onSave={(text) => {
                  if (text) posComment.mutate({ positionId: pos.id, comment: text });
                  setCommenting(null);
                }}
                onCancel={() => setCommenting(null)}
              />
            )}
          </td>
        </tr>
      )}
      {editingManual && pos.is_manual && (
        <tr className="bg-[var(--color-bg-elevated)]">
          <td colSpan={8} className="p-3">
            <ManualPositionForm
              pos={pos}
              method={method}
              kpPct={ctx.kpPct}
              zPct={ctx.zPct}
              pending={manual.update.isPending}
              error={
                manual.update.isError
                  ? errDetail(manual.update.error, "Nie udało się zapisać zmian.")
                  : ""
              }
              onCancel={() => setEditingManual(false)}
              onSubmit={(d) =>
                manual.update.mutate(
                  { positionId: pos.id, payload: d },
                  { onSuccess: () => setEditingManual(false) },
                )
              }
            />
          </td>
        </tr>
      )}
      {open && pos.is_manual && pos.manual_pricing_mode !== "inputs" && (
        <tr className="bg-[var(--color-bg-elevated)]">
          <td colSpan={8} className="px-3 py-2 text-[11px] font-mono">
            <span className="text-[var(--color-text-secondary)]">
              Pozycja dopisana ręcznie — cena jedn. {money(pos.manual_price)} zł ×
              obmiar {money(pos.quantity)} {pos.unit} ={" "}
              {money(pos.computed[ctx.variant]?.direct)} zł
            </span>
            {pos.manual_pricing_mode === "markup" ? (
              <span className="text-[var(--color-text-secondary)]">
                {"  "}+ Kp {money(pos.computed[ctx.variant]?.kp)} + Z{" "}
                {money(pos.computed[ctx.variant]?.z)} ={" "}
                <span className="font-semibold text-[var(--color-primary)]">
                  {money(pos.computed[ctx.variant]?.value)} zł
                </span>
              </span>
            ) : (
              <span className="text-[var(--color-text-muted)]">
                {"  "}— kompleksowa, narzutów (Kp/Z/Kz) nie doliczamy
              </span>
            )}
            {pos.matched_work_entry && pos.basis_raw && (
              <span className="text-[var(--color-text-muted)]">
                {"  "}· wzięta z naszej bazy cen ({pos.basis_raw})
              </span>
            )}
          </td>
        </tr>
      )}
      {open && !pos.is_manual && method === "simplified" && (
        <tr className="bg-[var(--color-bg-elevated)]">
          <td colSpan={8} className="px-3 py-2">
            <p className="text-[11px] text-[var(--color-text-secondary)] font-mono">
              Pozycja:{" "}
              <span className="text-[var(--color-text-primary)]">
                {pos.basis_raw || "—"}
              </span>
              {pos.match_status === "unmatched" && (
                <span className="text-amber-600"> — brak w cenniku</span>
              )}
              {ctx.canEdit && (
                <>
                  <button
                    className="ml-2 text-[var(--color-primary)] hover:underline"
                    onClick={() =>
                      setPicking((p) => (p === "__pos__" ? null : "__pos__"))
                    }
                  >
                    {pos.match_status === "unmatched" ? "Dopasuj ręcznie" : "Zmień"}
                  </button>
                  <button
                    className={`ml-2 hover:underline disabled:opacity-50 ${
                      pos.match_status === "unmatched" && pos.ai_price == null
                        ? "text-red-600 font-semibold animate-pulse"
                        : "text-amber-700"
                    }`}
                    title="Poproś AI o wycenę rynkową (web search + źródła)"
                    disabled={aiPricePosition.isPending}
                    onClick={() => aiPricePosition.mutate({ positionId: pos.id })}
                  >
                    {aiPricePosition.isPending ? "wyceniam…" : "wyceń AI"}
                  </button>
                </>
              )}
              {positionPriceButtons}
            </p>
            <AiPriceCard
              obj={pos}
              clearing={aiPricePosition.isPending}
              onClear={
                ctx.canEdit
                  ? () => aiPricePosition.mutate({ positionId: pos.id, clear: true })
                  : undefined
              }
            />
            <p className="mt-1 text-[11px] text-[var(--color-text-secondary)] font-mono">
              cena jedn. ({VARIANT_SHORT[ctx.variant]}){" "}
              {money(pos.computed[ctx.variant]?.price)} zł × obmiar{" "}
              {money(pos.quantity)} {pos.unit} ={" "}
              <span className="text-[var(--color-text-primary)] font-semibold">
                {money(pos.computed[ctx.variant]?.value)} zł
              </span>
              {"  "}(min {money(pos.computed.min?.price)} / maks{" "}
              {money(pos.computed.max?.price)} zł)
            </p>
            {positionPriceStatus}
            {picking === "__pos__" && (
              <MatchPicker
                catalogId={ctx.bcjCatalogId}
                kind="work_unit"
                onClose={() => setPicking(null)}
                onPick={(entryId) => {
                  matchPosition.mutate({ positionId: pos.id, entryId });
                  setPicking(null);
                }}
              />
            )}
          </td>
        </tr>
      )}
      {open &&
        method !== "simplified" &&
        (!pos.is_manual || pos.manual_pricing_mode === "inputs") && (
        <tr className="bg-[var(--color-bg-elevated)]">
          <td colSpan={8} className="p-3">
            <div className="card p-3">
              {pos.is_manual && (
                <p className="mb-2 text-[11px] text-red-700">
                  <span className="font-semibold">Pozycja dopisana ręcznie</span> —
                  wyceniana z własnych nakładów R/M/S: narzuty liczą się jak w
                  kosztorysie (Kp i Z od robocizny i sprzętu, Kz od materiałów), a
                  nakłady materiałowe wchodzą do zestawienia materiałów.
                </p>
              )}
              {pos.is_manual && pos.inputs.length === 0 && (
                <p className="mb-2 text-[11px] font-mono text-amber-600">
                  Nie dopisano jeszcze żadnego nakładu — pozycja liczy się na 0 zł.
                </p>
              )}
              <UnitMismatchPanel pos={pos} />
              <UnitScalePanel pos={pos} />
              <DeviceMismatchPanel pos={pos} />
              <ExpansionWarningsPanel pos={pos} />
              <BasisChangedPanel pos={pos} />
              {/* Pozycja wyceniona jako CAŁOŚĆ (nie z nakładów R/M/S) — analizator
                  nie zostawił nakładów, więc cenę dała AI na poziomie pozycji.
                  Bez tego bloku wycena wyglądała jak kwota bez uzasadnienia. */}
              {!pos.is_manual && (pos.inputs.length === 0 || pos.ai_price != null) && (
                <div className="mb-3 pb-2 border-b border-[var(--color-border)]">
                  <p className="text-[11px] text-[var(--color-text-secondary)] font-mono">
                    {pos.inputs.length === 0
                      ? "Pozycja bez nakładów (brak rozbicia R/M/S) — wyceniana w całości."
                      : "Cena rynkowa AI dla całej pozycji — informacyjnie; wartość liczona jest z nakładów niżej."}
                    {ctx.canEdit && (
                      <button
                        className={`ml-2 hover:underline disabled:opacity-50 ${
                          pos.ai_price == null
                            ? "text-red-600 font-semibold animate-pulse"
                            : "text-amber-700"
                        }`}
                        title="Poproś AI o wycenę rynkową całej pozycji (web search + źródła)"
                        disabled={aiPricePosition.isPending}
                        onClick={() =>
                          aiPricePosition.mutate({ positionId: pos.id })
                        }
                      >
                        {aiPricePosition.isPending
                          ? "wyceniam…"
                          : pos.ai_price == null
                            ? "wyceń AI"
                            : "wyceń AI ponownie"}
                      </button>
                    )}
                    {pos.inputs.length === 0 && positionPriceButtons}
                  </p>
                  <AiPriceCard
                    obj={pos}
                    clearing={aiPricePosition.isPending}
                    onClear={
                      ctx.canEdit
                        ? () =>
                            aiPricePosition.mutate({
                              positionId: pos.id,
                              clear: true,
                            })
                        : undefined
                    }
                  />
                  {pos.inputs.length === 0 &&
                    pos.ai_price != null &&
                    pos.manual_price == null &&
                    !pos.investor_supplied && (
                      <p className="mt-1 text-[11px] text-[var(--color-text-secondary)] font-mono">
                        cena jedn. ({VARIANT_SHORT[ctx.variant]}){" "}
                        {money(aiPriceFor(pos, ctx.variant))} zł × obmiar{" "}
                        {money(pos.quantity)} {pos.unit} ={" "}
                        <span className="text-[var(--color-text-primary)] font-semibold">
                          {money(pos.computed[ctx.variant]?.direct)} zł
                        </span>{" "}
                        kosztu bezpośredniego (
                        {pos.whole_price_markup === "labor"
                          ? "liczone jak robocizna — Kp i Z doliczone"
                          : "liczone jak materiał — tylko Kz"}
                        )
                      </p>
                    )}
                  {pos.inputs.length === 0 && positionPriceStatus}
                  {pos.inputs.length === 0 &&
                    !pos.investor_supplied &&
                    (pos.ai_price != null || pos.manual_price != null) && (
                      <p className="mt-1 text-[11px] font-mono text-[var(--color-text-secondary)]">
                        narzuty ryczałtu:{" "}
                        {ctx.canEdit ? (
                          <select
                            value={pos.whole_price_markup}
                            disabled={setWholeMarkup.isPending}
                            title="Jak liczyć narzuty pozycji wycenianej w całości: jak materiał (tylko koszty zakupu Kz) czy jak robocizna i sprzęt (koszty pośrednie Kp + zysk Z) — dla pozycji pracochłonnych (montaże, pomiary, geodezja) wybierz drugie"
                            onChange={(e) =>
                              setWholeMarkup.mutate({
                                positionId: pos.id,
                                mode: e.target.value as "material" | "labor",
                              })
                            }
                            className="bg-transparent text-[11px] text-[var(--color-primary)] cursor-pointer border border-[var(--color-border)] px-1 py-0.5"
                          >
                            <option value="material">
                              jak materiał — tylko Kz
                            </option>
                            <option value="labor">
                              jak robocizna i sprzęt — Kp + Z
                            </option>
                          </select>
                        ) : pos.whole_price_markup === "labor" ? (
                          "jak robocizna i sprzęt (Kp + Z)"
                        ) : (
                          "jak materiał (tylko Kz)"
                        )}
                      </p>
                    )}
                  {pos.inputs.length === 0 &&
                    pos.ai_price == null &&
                    pos.manual_price == null &&
                    !pos.investor_supplied && (
                      <p className="mt-1 text-[11px] text-amber-600 font-mono">
                        Brak nakładów i brak ceny — pozycja liczona na 0 zł. Użyj
                        „wyceń AI”, wpisz cenę ręcznie albo oznacz jako dostawę
                        inwestorską.
                      </p>
                    )}
                </div>
              )}
              {!pos.is_manual && pos.inputs.length > 0 && (
                <p className="mb-2 text-[11px] font-mono">
                  {pos.investor_supplied ? (
                    <span className="text-blue-600">
                      dostawa inwestorska — cała pozycja (wszystkie nakłady)
                      liczona jawnie po 0 zł
                      {ctx.canEdit && (
                        <button
                          className="ml-1.5 text-[var(--color-primary)] hover:underline"
                          onClick={() =>
                            posInvestor.mutate({ positionId: pos.id, value: false })
                          }
                        >
                          cofnij
                        </button>
                      )}
                    </span>
                  ) : ctx.canEdit ? (
                    <button
                      className="text-blue-600 hover:underline"
                      title="Oznacz CAŁĄ pozycję (wszystkie jej nakłady) jako dostawę inwestorską — 0 zł"
                      onClick={() =>
                        posInvestor.mutate({ positionId: pos.id, value: true })
                      }
                    >
                      cała pozycja inwestorska
                    </button>
                  ) : null}
                </p>
              )}
              {pos.inputs.length > 0 && (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                    <th className="text-left font-medium pb-1.5">Nakład</th>
                    <th className="text-right font-medium pb-1.5">Nakłady</th>
                    <th className="text-right font-medium pb-1.5">
                      Cena {VARIANT_SHORT[ctx.variant]}
                    </th>
                    <th className="text-right font-medium pb-1.5">
                      Wartość {VARIANT_SHORT[ctx.variant]}
                    </th>
                    <th className="text-left pl-3 font-medium pb-1.5">
                      Dopasowanie do cennika
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pos.inputs.map((inp) => (
                    <tr key={inp.id}>
                      <td className="py-1 text-[var(--color-text-primary)]">
                        {renaming === inp.id ? (
                          <span className="flex items-start gap-1.5">
                            <textarea
                              autoFocus
                              rows={2}
                              value={nameText}
                              onChange={(e) => setNameText(e.target.value)}
                              className="flex-1 border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-1"
                            />
                            <button
                              className="text-[11px] text-[var(--color-primary)] hover:underline"
                              disabled={
                                inputDescription.isPending || !nameText.trim()
                              }
                              onClick={() =>
                                inputDescription.mutate(
                                  {
                                    inputId: inp.id,
                                    description: nameText.trim(),
                                  },
                                  { onSuccess: () => setRenaming(null) },
                                )
                              }
                            >
                              zapisz
                            </button>
                            <button
                              className="text-[11px] text-[var(--color-text-muted)] hover:underline"
                              onClick={() => setRenaming(null)}
                            >
                              anuluj
                            </button>
                          </span>
                        ) : (
                          <>
                            <KindTag kind={inp.kind} />
                            <span
                              className={
                                inp.excluded
                                  ? "line-through text-[var(--color-text-muted)]"
                                  : undefined
                              }
                            >
                              {inp.description}
                            </span>
                            {inp.is_percent_aux ? " (%)" : ""}
                            {inp.is_manual && (
                              <>
                                {" "}
                                <ManualBadge
                                  label="RĘCZNY"
                                  title="Nakład dopisany ręcznie przez kosztorysanta — nie ma go w dokumencie ani w normie"
                                />
                              </>
                            )}
                            {(ctx.canEdit || inp.description_original) && (
                              <span className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                                {ctx.canEdit && (
                                  <button
                                    className="text-[10px] text-[var(--color-primary)] hover:underline"
                                    title="Zmień nazwę nakładu (nazwa z dokumentu zostanie zachowana)"
                                    onClick={() => {
                                      setNameText(inp.description);
                                      setRenaming(inp.id);
                                    }}
                                  >
                                    zmień nazwę
                                  </button>
                                )}
                                {ctx.canEdit && !inp.is_percent_aux && (
                                  <select
                                    value={inp.kind}
                                    disabled={setInputKind.isPending}
                                    title="Rodzaj nakładu decyduje o narzutach: Kp i Z liczą się od robocizny i sprzętu, Kz od materiałów. Usługę obcą wpisaną w dokumencie w sekcję materiałową (geodezja, projekt organizacji ruchu, zajęcie pasa drogowego) przenieś do robocizny — cena nakładu zostaje bez zmian."
                                    onChange={(e) =>
                                      setInputKind.mutate({
                                        inputId: inp.id,
                                        kind: e.target.value as
                                          | "labor"
                                          | "material"
                                          | "equipment",
                                      })
                                    }
                                    className="bg-transparent text-[10px] text-[var(--color-primary)] border border-[var(--color-border)] px-1 py-px cursor-pointer"
                                  >
                                    <option value="labor">R — robocizna</option>
                                    <option value="material">M — materiał</option>
                                    <option value="equipment">S — sprzęt</option>
                                  </select>
                                )}
                                {ctx.canEdit && inp.is_manual && (
                                  <button
                                    className="text-[10px] text-red-600 hover:underline disabled:opacity-50"
                                    title="Usuń ten dopisany nakład z pozycji"
                                    disabled={manualInputs.remove.isPending}
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `Usunąć dopisany nakład „${inp.description}”?`,
                                        )
                                      )
                                        manualInputs.remove.mutate(inp.id);
                                    }}
                                  >
                                    usuń nakład
                                  </button>
                                )}
                                {inp.description_original && (
                                  <ManualBadge
                                    label="NAZWA ZMIENIONA"
                                    title={`Nazwa zmieniona ręcznie — nazwa z dokumentu: ${inp.description_original}`}
                                  />
                                )}
                                {ctx.canEdit && inp.description_original && (
                                  <button
                                    className="text-[9px] text-[var(--color-text-muted)] hover:underline"
                                    disabled={inputDescription.isPending}
                                    title="Przywróć nazwę z dokumentu"
                                    onClick={() =>
                                      inputDescription.mutate({
                                        inputId: inp.id,
                                        description: "",
                                      })
                                    }
                                  >
                                    przywróć
                                  </button>
                                )}
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="py-1 text-right tabular-nums whitespace-nowrap align-top">
                        {editingNorm === inp.id ? (
                          <span className="flex items-center justify-end gap-1">
                            <input
                              autoFocus
                              value={normText}
                              onChange={(e) => setNormText(e.target.value)}
                              placeholder="ilość"
                              className="w-16 border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-0.5 text-right tabular-nums"
                            />
                            <button
                              className="text-[10px] text-[var(--color-primary)] hover:underline"
                              disabled={setInputNorm.isPending || !normText.trim()}
                              onClick={() =>
                                setInputNorm.mutate(
                                  {
                                    inputId: inp.id,
                                    normTotal: normText.trim(),
                                  },
                                  {
                                    onSuccess: () => setEditingNorm(null),
                                    onError: (er: unknown) =>
                                      window.alert(
                                        errDetail(
                                          er,
                                          "Nie udało się zmienić nakładu.",
                                        ),
                                      ),
                                  },
                                )
                              }
                            >
                              zapisz
                            </button>
                            <button
                              className="text-[10px] text-[var(--color-text-muted)] hover:underline"
                              onClick={() => setEditingNorm(null)}
                            >
                              anuluj
                            </button>
                          </span>
                        ) : (
                          <>
                            <span
                              className={
                                inp.norm_total_original != null
                                  ? "font-semibold text-red-600"
                                  : undefined
                              }
                            >
                              {money(inp.norm_total)} {inp.unit}
                            </span>
                            {ctx.canEdit && (
                              <button
                                className="ml-1 text-[10px] text-[var(--color-primary)] hover:underline"
                                title="Zmień ilość nakładu (np. roboczogodziny) — wartość = ilość × cena jedn., więc zwiększenie nakładu podnosi cenę pozycji"
                                onClick={() => {
                                  setNormText(
                                    String(inp.norm_total).replace(".", ","),
                                  );
                                  setEditingNorm(inp.id);
                                }}
                              >
                                zmień
                              </button>
                            )}
                            {inp.norm_total_original != null && (
                              <div className="mt-0.5">
                                <ManualBadge
                                  label="NAKŁAD ZMIENIONY"
                                  title={`Nakład zmieniony ręcznie — z dokumentu: ${money(
                                    inp.norm_total_original,
                                  )} ${inp.unit}`}
                                />
                                {ctx.canEdit && (
                                  <button
                                    className="ml-1 text-[9px] text-[var(--color-text-muted)] hover:underline"
                                    title={`Przywróć nakład z dokumentu (${money(
                                      inp.norm_total_original,
                                    )} ${inp.unit})`}
                                    disabled={setInputNorm.isPending}
                                    onClick={() =>
                                      setInputNorm.mutate({
                                        inputId: inp.id,
                                        normTotal: null,
                                      })
                                    }
                                  >
                                    przywróć
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="py-1 text-right tabular-nums">
                        {inp.is_percent_aux
                          ? "—"
                          : money(inp.unit_price[ctx.variant])}
                      </td>
                      <td className="py-1 text-right font-medium tabular-nums text-[var(--color-text-primary)]">
                        {money(inp.subtotal[ctx.variant])}
                      </td>
                      <td className="pl-3 py-1 text-[var(--color-text-secondary)] align-top">
                        {inp.excluded ? (
                          <div className="whitespace-nowrap">
                            <span className="text-[var(--color-text-muted)] line-through">
                              wykreślony
                            </span>
                            <span className="ml-1 text-[var(--color-text-muted)]">
                              — 0 zł
                            </span>
                            {ctx.canEdit && (
                              <button
                                className="ml-1.5 text-[var(--color-primary)] hover:underline"
                                onClick={() =>
                                  setExcluded.mutate({
                                    inputId: inp.id,
                                    excluded: false,
                                  })
                                }
                              >
                                przywróć
                              </button>
                            )}
                          </div>
                        ) : inp.investor_supplied ? (
                          <div className="whitespace-nowrap">
                            <span className="text-blue-600">
                              dostawa inwestorska — 0 zł
                            </span>
                            {ctx.canEdit && inp.kind !== "labor" && !inp.is_percent_aux && (
                              <button
                                className="ml-1.5 text-[var(--color-primary)] hover:underline"
                                onClick={() =>
                                  investor.mutate({
                                    inputId: inp.id,
                                    value: false,
                                  })
                                }
                              >
                                cofnij
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className="whitespace-nowrap">
                              <span
                                className={
                                  inp.match_status === "unmatched"
                                    ? "text-amber-600"
                                    : inp.match_status === "fuzzy" ||
                                        inp.match_status === "ai"
                                      ? "text-[var(--color-text-secondary)]"
                                      : "text-emerald-600"
                                }
                              >
                                {matchLabel(inp.match_status, inp.match_score)}
                              </span>
                              {ctx.canEdit && inp.kind !== "labor" && !inp.is_percent_aux && (
                                <>
                                  <button
                                    className="ml-1.5 text-[var(--color-primary)] hover:underline"
                                    onClick={() =>
                                      setPicking((p) =>
                                        p === inp.id ? null : inp.id,
                                      )
                                    }
                                  >
                                    {inp.match_status === "unmatched"
                                      ? "dopasuj"
                                      : "zmień"}
                                  </button>
                                  <button
                                    className="ml-1.5 text-blue-600 hover:underline"
                                    title="Oznacz jako dostawę inwestorską (0 zł)"
                                    onClick={() =>
                                      investor.mutate({
                                        inputId: inp.id,
                                        value: true,
                                      })
                                    }
                                  >
                                    inwestorska
                                  </button>
                                  <button
                                    className="ml-1.5 text-[var(--color-text-muted)] hover:underline"
                                    title="Wykreśl nakład z wyceny (0 zł) — np. sprzęt zbędny w tych warunkach; zostaje widoczny jako przekreślony"
                                    onClick={() =>
                                      setExcluded.mutate({
                                        inputId: inp.id,
                                        excluded: true,
                                      })
                                    }
                                  >
                                    wykreśl
                                  </button>
                                  <button
                                    className="ml-1.5 text-[var(--color-text-secondary)] hover:underline"
                                    title="Wpisz cenę jednostkową ręcznie (nadpisuje cennik)"
                                    onClick={() => {
                                      setPriceText(inp.manual_price ?? "");
                                      setPricing((p) => (p === inp.id ? null : inp.id));
                                    }}
                                  >
                                    wpisz ręcznie
                                  </button>
                                  <button
                                    className={`ml-1.5 hover:underline disabled:opacity-50 ${
                                      inp.match_status === "unmatched"
                                        ? "text-red-600 font-semibold animate-pulse"
                                        : "text-amber-700"
                                    }`}
                                    title="Poproś AI o wycenę rynkową (web search + źródła)"
                                    disabled={aiPriceInput.isPending}
                                    onClick={() =>
                                      aiPriceInput.mutate({ inputId: inp.id })
                                    }
                                  >
                                    {aiPriceInput.isPending ? "wyceniam…" : "wyceń AI"}
                                  </button>
                                  <button
                                    className="ml-1.5 text-emerald-700 hover:underline"
                                    title="Zapisz tę pozycję z ceną do naszej bazy — w kolejnych wycenach dopasuje się automatycznie"
                                    onClick={() =>
                                      setSavingBase((p) =>
                                        p === inp.id ? null : inp.id,
                                      )
                                    }
                                  >
                                    do naszej bazy
                                  </button>
                                </>
                              )}
                              {ctx.canEdit &&
                                !inp.comment &&
                                commenting !== inp.id && (
                                  <button
                                    className="ml-1.5 text-violet-700 hover:underline"
                                    title="Dodaj komentarz do tego nakładu (widoczny w wycenie i PDF)"
                                    onClick={() => setCommenting(inp.id)}
                                  >
                                    💬 komentarz
                                  </button>
                                )}
                            </div>
                            {inp.comment && (
                              <CommentBubble
                                className="mt-1"
                                comment={inp.comment}
                                canEdit={ctx.canEdit}
                                pending={inputComment.isPending}
                                onSave={(text) =>
                                  inputComment.mutate({
                                    inputId: inp.id,
                                    comment: text,
                                  })
                                }
                              />
                            )}
                            {!inp.comment && commenting === inp.id && (
                              <div className="mt-1">
                                <NewCommentEditor
                                  pending={inputComment.isPending}
                                  onSave={(text) => {
                                    if (text)
                                      inputComment.mutate({
                                        inputId: inp.id,
                                        comment: text,
                                      });
                                    setCommenting(null);
                                  }}
                                  onCancel={() => setCommenting(null)}
                                />
                              </div>
                            )}
                            {inp.manual_price != null && (
                              <div className="text-[10px] text-red-600">
                                cena ręczna: {money(inp.manual_price)} zł{" "}
                                {ctx.canEdit && (
                                  <button
                                    className="text-[var(--color-primary)] hover:underline"
                                    onClick={() => manualPrice.mutate({ inputId: inp.id, price: null })}
                                  >
                                    usuń
                                  </button>
                                )}
                              </div>
                            )}
                            <AiPriceCard
                              obj={inp}
                              clearing={aiPriceInput.isPending}
                              onClear={
                                ctx.canEdit
                                  ? () =>
                                      aiPriceInput.mutate({
                                        inputId: inp.id,
                                        clear: true,
                                      })
                                  : undefined
                              }
                            />
                            {savingBase === inp.id && (
                              <SaveToBasePanel
                                inp={inp}
                                jobId={ctx.jobId}
                                onClose={() => setSavingBase(null)}
                              />
                            )}
                            {pricing === inp.id && (
                              <div className="mt-1 flex items-center gap-1">
                                <input
                                  autoFocus
                                  value={priceText}
                                  onChange={(e) => setPriceText(e.target.value)}
                                  placeholder="cena jedn. zł"
                                  className="w-24 border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[11px] px-1.5 py-0.5"
                                />
                                <button
                                  className="text-[11px] text-[var(--color-primary)] hover:underline"
                                  onClick={() => {
                                    manualPrice.mutate({ inputId: inp.id, price: priceText.trim() || null });
                                    setPricing(null);
                                  }}
                                >
                                  zapisz
                                </button>
                                <button
                                  className="text-[11px] text-[var(--color-text-muted)] hover:underline"
                                  onClick={() => setPricing(null)}
                                >
                                  anuluj
                                </button>
                              </div>
                            )}
                            {inp.matched_code && (
                              <div
                                className="text-[10px] text-[var(--color-text-muted)] max-w-[280px] truncate"
                                title={`${inp.matched_code} ${inp.matched_description ?? ""}`}
                              >
                                →{" "}
                                <span className="font-mono">
                                  {inp.matched_code}
                                </span>{" "}
                                {inp.matched_description}
                                {inp.matched_price_avg != null &&
                                  ` (śr ${money(inp.matched_price_avg)} zł)`}
                              </div>
                            )}
                            {ctx.canEdit &&
                              inp.match_status === "unmatched" &&
                              inp.suggestions?.length > 0 && (
                                <div className="mt-1">
                                  <div className="text-[10px] text-[var(--color-text-muted)]">
                                    Propozycje (kliknij, by wybrać):
                                  </div>
                                  {inp.suggestions.map((s) => (
                                    <button
                                      key={s.entry_id}
                                      title={`${s.code} ${s.description}${
                                        s.price_avg != null
                                          ? ` — cena jedn. (śr) ${money(s.price_avg)} zł${
                                              s.unit ? `/${s.unit}` : ""
                                            }`
                                          : ""
                                      }`}
                                      className="block text-left text-[10px] text-[var(--color-primary)] hover:underline max-w-[320px] truncate"
                                      onClick={() =>
                                        matchInput.mutate({
                                          inputId: inp.id,
                                          entryId: s.entry_id,
                                        })
                                      }
                                    >
                                      {s.score.toFixed(0)}% ·{" "}
                                      <span className="font-mono">
                                        {s.code}
                                      </span>{" "}
                                      {s.description}
                                      {s.price_avg != null &&
                                        ` (śr ${money(s.price_avg)} zł${
                                          s.unit ? `/${s.unit}` : ""
                                        })`}
                                    </button>
                                  ))}
                                </div>
                              )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
              {picking && picking !== "__pos__" && (
                <MatchPicker
                  catalogId={ctx.rmsCatalogId}
                  kind={pickKind}
                  onClose={() => setPicking(null)}
                  onPick={(entryId) => {
                    matchInput.mutate({ inputId: picking, entryId });
                    setPicking(null);
                  }}
                />
              )}
              {/* Rozbicie pozycji dopisanej ręcznie na materiał / robociznę / sprzęt
                  — koszt zakupu szafki osobno od kosztu jej montażu. */}
              {pos.is_manual && ctx.canEdit && !addingInput && (
                <button
                  className="mt-2 text-[11px] text-red-600 font-medium hover:underline"
                  onClick={() => setAddingInput(true)}
                >
                  + dodaj nakład (materiał / robocizna / sprzęt)
                </button>
              )}
              {pos.is_manual && ctx.canEdit && addingInput && (
                <ManualInputForm
                  pending={manualInputs.create.isPending}
                  error={
                    manualInputs.create.isError
                      ? errDetail(
                          manualInputs.create.error,
                          "Nie udało się dodać nakładu.",
                        )
                      : ""
                  }
                  onCancel={() => setAddingInput(false)}
                  onSubmit={(d) =>
                    manualInputs.create.mutate(
                      { positionId: pos.id, payload: d },
                      { onSuccess: () => setAddingInput(false) },
                    )
                  }
                />
              )}
              {pos.computed[ctx.variant] && (
                <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex justify-end">
                  <dl className="w-full max-w-xs text-[11px] space-y-0.5">
                    <ProofLine
                      label="Koszt bezpośredni (R+M+S)"
                      value={pos.computed[ctx.variant]!.direct}
                    />
                    <ProofLine
                      label="+ Koszty pośrednie (Kp)"
                      value={pos.computed[ctx.variant]!.kp}
                    />
                    <ProofLine
                      label="+ Zysk (Z)"
                      value={pos.computed[ctx.variant]!.z}
                    />
                    <ProofLine
                      label="+ Koszty zakupu (Kz)"
                      value={pos.computed[ctx.variant]!.kz}
                    />
                    <div className="flex justify-between gap-6 border-t border-[var(--color-border)] pt-1 mt-1 font-semibold">
                      <dt className="text-[var(--color-text-primary)]">
                        = Wartość pozycji ({VARIANT_SHORT[ctx.variant]})
                      </dt>
                      <dd className="tabular-nums text-[var(--color-primary)]">
                        {money(pos.computed[ctx.variant]!.value)} zł
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function RouteLengthEditor({
  est,
  ctx,
}: {
  est: EstimateResult;
  ctx: MatchCtx;
}) {
  const setRoute = useSetRouteLength(ctx.jobId);
  const [editing, setEditing] = useState(false);
  const [openT, setOpenT] = useState("");
  const [bore, setBore] = useState("");
  const a = est.analytics as { line_rate_per_m?: string | null };
  const hasRoute = est.route_length_m != null;
  const startEdit = () => {
    setOpenT(est.route_open_trench_m ?? "");
    setBore(est.route_bore_m ?? "");
    setEditing(true);
  };
  return (
    <div className="px-4 py-2 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] text-xs flex items-center gap-2 flex-wrap">
      <span className="section-label">Trasa (długość trasowa)</span>
      {editing ? (
        <span className="flex items-center gap-1 flex-wrap">
          <label className="flex items-center gap-1">
            wykop otw.
            <input
              autoFocus
              value={openT}
              onChange={(e) => setOpenT(e.target.value)}
              placeholder="mb"
              className="w-16 border border-[var(--color-border)] bg-[var(--color-bg-base)] px-1.5 py-0.5"
            />
          </label>
          <label className="flex items-center gap-1">
            przewierty
            <input
              value={bore}
              onChange={(e) => setBore(e.target.value)}
              placeholder="mb"
              className="w-16 border border-[var(--color-border)] bg-[var(--color-bg-base)] px-1.5 py-0.5"
            />
          </label>
          <button
            className="text-[var(--color-primary)] hover:underline"
            onClick={() => {
              setRoute.mutate({
                estimateId: est.id,
                route_open_trench_m: openT.trim() || null,
                route_bore_m: bore.trim() || null,
              });
              setEditing(false);
            }}
          >
            zapisz
          </button>
          <button
            className="text-[var(--color-text-muted)] hover:underline"
            onClick={() => setEditing(false)}
          >
            anuluj
          </button>
        </span>
      ) : (
        <>
          <span className="tabular-nums text-[var(--color-text-secondary)]">
            {est.route_open_trench_m != null && (
              <>wykop otw. {money(est.route_open_trench_m)} mb </>
            )}
            {est.route_bore_m != null && (
              <>· przewierty {money(est.route_bore_m)} mb </>
            )}
          </span>
          <span className="tabular-nums font-medium text-[var(--color-text-primary)]">
            {hasRoute
              ? `= ${money(est.route_length_m)} mb`
              : ctx.canEdit
                ? "— brak (wpisz, by policzyć zł/mb)"
                : "— brak"}
          </span>
          {ctx.canEdit && (
            <button
              className="text-[var(--color-primary)] hover:underline"
              onClick={startEdit}
            >
              {hasRoute ? "zmień" : "wpisz"}
            </button>
          )}
          {hasRoute && a?.line_rate_per_m && (
            <span className="text-[var(--color-text-secondary)] tabular-nums">
              · stawka{" "}
              <span className="font-semibold text-[var(--color-primary)]">
                {money(a.line_rate_per_m)} zł/mb
              </span>
            </span>
          )}
          {est.route_length_note && (
            <span className="text-[var(--color-text-muted)]">
              ({est.route_length_note})
            </span>
          )}
        </>
      )}
    </div>
  );
}

function EstimateBlock({ est, ctx }: { est: EstimateResult; ctx: MatchCtx }) {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const manual = useManualPositions(ctx.jobId);
  const manualCount = est.positions.filter((p) => p.is_manual).length;
  const allOpen =
    est.positions.length > 0 && openIds.size === est.positions.length;
  const toggleAll = () =>
    setOpenIds(allOpen ? new Set() : new Set(est.positions.map((p) => p.id)));
  const toggleOne = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="card">
      <div className="px-4 py-3 bg-[var(--color-bg-elevated)] border-b border-[var(--color-border)] flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2 flex-wrap">
          {est.title}
          {est.page_range && (
            <span className="text-[var(--color-text-muted)] font-normal">
              (s. {est.page_range})
            </span>
          )}
          <span className="section-label border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-0.5">
            {est.method_display}
          </span>
          {/* Provenance of the nakłady — full kosztorys the client gave us vs a
              bare przedmiar we expanded ourselves. Transparency for tender trust. */}
          {est.naklady_source === "document" && (
            <span className="section-label border border-green-300 bg-green-50 text-green-700 px-1.5 py-0.5">
              ✓ z pełnego kosztorysu nakładczego
            </span>
          )}
          {est.naklady_source === "norms" && (
            <span
              className="section-label border border-amber-300 bg-amber-50 text-amber-700 px-1.5 py-0.5"
              title="Nakłady R/M/S nie były w dokumencie — rozwinęliśmy je z obmiaru przez normy KNR/KNNR. Zweryfikuj, zwłaszcza pozycje o dziwnie niskiej robociźnie."
            >
              ⚠ z obmiaru — rozwinięte z norm KNR/KNNR
            </span>
          )}
        </span>
        <span className="text-sm font-semibold text-[var(--color-primary)] whitespace-nowrap tabular-nums">
          {VARIANT_SHORT[ctx.variant]} {money(est.valuation?.[ctx.variant]?.total)} zł
        </span>
      </div>
      <RouteLengthEditor est={est} ctx={ctx} />
      {est.warnings?.length > 0 && (
        <ul className="px-4 py-2 text-[11px] text-amber-700 space-y-0.5 border-b border-[var(--color-border)] bg-amber-50">
          {est.warnings.map((w, i) => (
            <li key={i}>⚠ {w}</li>
          ))}
        </ul>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[var(--color-text-muted)] bg-[var(--color-bg-surface)]">
              <th className="px-2 py-2 text-left font-medium">Lp</th>
              <th className="px-2 py-2 text-left font-medium">Podstawa</th>
              <th className="px-2 py-2 text-left font-medium">Opis</th>
              <th className="px-2 py-2 text-right font-medium">Obmiar</th>
              {VARIANTS.map((v) => (
                <th
                  key={v.key}
                  className={`px-2 py-2 text-right font-medium whitespace-nowrap ${
                    v.key === ctx.variant ? "text-[var(--color-primary)]" : ""
                  }`}
                >
                  Wart. {VARIANT_SHORT[v.key]}
                  {v.key === ctx.variant && " (oferta)"}
                </th>
              ))}
              <th className="px-2 py-2 text-right">
                <button
                  className="text-[11px] font-normal normal-case text-[var(--color-primary)] hover:underline whitespace-nowrap"
                  onClick={toggleAll}
                >
                  {allOpen ? "Zwiń wszystkie" : "Rozwiń wszystkie"}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {est.positions.map((p) => (
              <PositionRow
                key={p.id}
                pos={p}
                method={est.method}
                ctx={ctx}
                open={openIds.has(p.id)}
                onToggle={() => toggleOne(p.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
      {/* Ręczne dopisanie pozycji — „czegoś brakuje w kosztorysie” / korekta w dół. */}
      <div className="px-4 py-2 border-t border-[var(--color-border)] bg-[var(--color-bg-surface)] space-y-2">
        <div className="flex items-center justify-between gap-3 text-[11px]">
          <span className="text-[var(--color-text-muted)]">
            {manualCount > 0
              ? `Pozycji dopisanych ręcznie: ${manualCount} — oznaczone na czerwono w tabeli.`
              : "Brakuje pozycji w kosztorysie inwestora? Dopisz ją z naszej bazy cen albo z ręki."}
          </span>
          {ctx.canEdit && !adding && (
            <button
              className="text-[var(--color-primary)] font-medium hover:underline whitespace-nowrap"
              onClick={() => setAdding(true)}
            >
              + Dodaj pozycję
            </button>
          )}
        </div>
        {adding && (
          <ManualPositionForm
            method={est.method}
            kpPct={ctx.kpPct}
            zPct={ctx.zPct}
            pending={manual.create.isPending}
            error={
              manual.create.isError
                ? errDetail(manual.create.error, "Nie udało się dodać pozycji.")
                : ""
            }
            onCancel={() => setAdding(false)}
            onSubmit={(d) =>
              manual.create.mutate(
                { estimateId: est.id, payload: d },
                { onSuccess: () => setAdding(false) },
              )
            }
          />
        )}
      </div>
    </div>
  );
}

// Strony, z których faktycznie wyciągamy pozycje do wyceny — te same klasy co
// BIDDABLE_CLASSIFICATIONS na backendzie. Sam przedmiar (bez nakładów R/M/S) też
// jest kosztorysem do wyceny, więc nie może pokazywać się jako „brak kosztorysu".
const BIDDABLE_PAGE_CLASSES = [
  "kosztorys_nakladczy",
  "przedmiar",
  "kosztorys_inwestorski",
];

const biddablePages = (doc: EstimateDocument) =>
  doc.pages
    .filter(
      (p) =>
        p.is_kosztorys_nakladczy ||
        BIDDABLE_PAGE_CLASSES.includes(p.classification),
    )
    .map((p) => p.page_number);

function DocumentsSection({
  documents,
  sourceFiles,
}: {
  documents: EstimateDocument[];
  sourceFiles: EstimateJobDetail["files"];
}) {
  const [open, setOpen] = useState(false);
  const [fetchingId, setFetchingId] = useState<string | null>(null);
  const total = documents.length;
  const withK = documents.filter((d) => biddablePages(d).length > 0).length;
  // Documents already cover every uploaded PDF one-to-one; only ZIP archives
  // exist solely in job.files, so just those get their own download rows.
  const zipFiles = sourceFiles.filter((f) => f.kind === "zip" && f.managed_file);

  const download = async (managedFileId: string, filename: string) => {
    setFetchingId(managedFileId);
    try {
      const { url } = await getManagedFileUrl(managedFileId);
      if (!url) {
        window.alert(
          "Plik nie jest jeszcze dostępny do pobrania — spróbuj ponownie za chwilę.",
        );
        return;
      }
      triggerBrowserDownload(url, filename);
    } finally {
      setFetchingId(null);
    }
  };

  const downloadButton = (managedFileId: string, filename: string) => (
    <button
      className="text-xs text-[var(--color-primary)] hover:underline whitespace-nowrap disabled:opacity-50"
      disabled={fetchingId !== null}
      onClick={() => download(managedFileId, filename)}
      title="Pobierz plik źródłowy"
    >
      {fetchingId === managedFileId ? "Pobieranie…" : "Pobierz"}
    </button>
  );

  return (
    <div className="card">
      <button
        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-[var(--color-bg-base)]"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex items-center gap-2">
          <span className="text-[var(--color-text-muted)] text-xs w-3">
            {open ? "▾" : "▸"}
          </span>
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">
            Rozpoznane dokumenty
          </span>
        </span>
        <span className="text-xs text-[var(--color-text-secondary)] tabular-nums">
          {total} {plural(total, "plik", "pliki", "plików")}
          {withK > 0 ? (
            <span className="text-emerald-600">
              {" · "}kosztorys w {withK}{" "}
              {plural(withK, "pliku", "plikach", "plikach")}
            </span>
          ) : (
            <span className="text-[var(--color-text-muted)]">
              {" · "}brak kosztorysu
            </span>
          )}
        </span>
      </button>
      {open && (
        <ul className="border-t border-[var(--color-border)] divide-y divide-[var(--color-border)]">
          {zipFiles.map((f) => (
            <li
              key={f.id}
              className="px-4 py-2 text-sm flex items-baseline justify-between gap-3"
            >
              <span className="text-[var(--color-text-primary)] break-all">
                {f.original_filename}{" "}
                <span className="text-[var(--color-text-muted)] whitespace-nowrap">
                  (archiwum ZIP, {f.document_count}{" "}
                  {plural(f.document_count, "plik", "pliki", "plików")})
                </span>
              </span>
              {downloadButton(f.managed_file!, f.original_filename)}
            </li>
          ))}
          {documents.map((doc) => {
            const kPages = biddablePages(doc);
            return (
              <li
                key={doc.id}
                className="px-4 py-2 text-sm flex items-baseline justify-between gap-3"
              >
                <span className="text-[var(--color-text-primary)] break-all">
                  {doc.filename}{" "}
                  <span className="text-[var(--color-text-muted)] whitespace-nowrap">
                    ({doc.page_count} s.)
                  </span>
                </span>
                <span className="flex items-baseline gap-3 whitespace-nowrap">
                  {kPages.length > 0 ? (
                    <span className="text-emerald-600">
                      str. {kPages.join(", ")}
                    </span>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">—</span>
                  )}
                  {doc.managed_file &&
                    downloadButton(doc.managed_file, doc.filename)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Kolory wyniku przetargu — te same na plakietce statusu i na przełączniku.
 *  `active` nadpisuje wariant przycisku (stąd `!`), więc zapalony wynik zostaje
 *  przyciskiem tej samej wysokości co reszta paska. */
const OUTCOME_STYLE: Record<TenderOutcome, { badge: string; active: string }> = {
  won: { badge: "bg-emerald-600 text-white", active: "!bg-emerald-600 !text-white !border-emerald-600" },
  lost: { badge: "bg-rose-600 text-white", active: "!bg-rose-600 !text-white !border-rose-600" },
  cancelled: { badge: "bg-slate-600 text-white", active: "!bg-slate-600 !text-white !border-slate-600" },
};

const OUTCOME_HINT: Record<TenderOutcome, string> = {
  won: "Przetarg rozstrzygnięty na naszą korzyść — wycena zamknięta jako wygrana",
  lost: "Wygrał kto inny — wycena zamknięta jako przegrana (kwota oferty zostaje do porównań)",
  cancelled: "Zamawiający unieważnił / anulował przetarg — nie ma czego rozstrzygać",
};

/** Wynik przetargu — stan KOŃCOWY wyceny. Osobno od GOTOWE/ZŁOŻONY, bo tamte mówią,
 *  co MY zrobiliśmy z wyceną, a ten mówi, co zamawiający zrobił z ofertą. Kliknięcie
 *  zapalonego wyniku cofa go (wpisany pomyłkowo albo rozstrzygnięcie uchylone). */
function OutcomePicker({ job }: { job: EstimateJobDetail }) {
  const setOutcome = useSetJobOutcome(job.id);
  // Fragment, nie własny kontener: przyciski wchodzą wprost do paska akcji, więc
  // zawijają się razem z resztą i mają tę samą wysokość.
  return (
    <>
      <span className="ml-1 text-xs text-[var(--color-text-muted)]">
        Wynik przetargu:
      </span>
      {(Object.keys(OUTCOME_LABELS) as TenderOutcome[]).map((key) => {
        const active = job.outcome === key;
        // Wygranej ani przegranej nie ma bez złożonej oferty (backend odrzuca);
        // anulowanie zamawiający ogłasza także przed terminem składania.
        const blocked = key !== "cancelled" && !job.is_submitted;
        return (
          <Button
            key={key}
            variant="secondary"
            size="sm"
            disabled={blocked}
            loading={setOutcome.isPending && setOutcome.variables === (active ? null : key)}
            onClick={() => setOutcome.mutate(active ? null : key)}
            className={active ? OUTCOME_STYLE[key].active : ""}
            title={
              blocked
                ? "Najpierw oznacz ofertę jako złożoną — bez oferty przetarg nie ma dla nas wyniku"
                : active
                  ? "Kliknij, żeby cofnąć wynik"
                  : OUTCOME_HINT[key]
            }
          >
            {OUTCOME_LABELS[key]}
          </Button>
        );
      })}
      {setOutcome.isError && (
        <p className="basis-full text-[11px] text-red-600">
          Nie udało się zapisać wyniku — spróbuj ponownie.
        </p>
      )}
    </>
  );
}

/** `jobId` w propsie pozwala osadzić tę stronę tam, gdzie kosztorys jest częścią
 *  większego widoku (zakładka w warsztacie przetargu) — bez propsa działa jak dotąd,
 *  czytając id z trasy. Osadzenie chowa też pola, które w tym kontekście są zbędne:
 *  termin oferty i link do przetargu są tuż obok, na karcie samego przetargu. */
export function EstimateResultPage({ jobId: jobIdProp }: { jobId?: string } = {}) {
  const { jobId: routeJobId } = useParams<{ jobId: string }>();
  const jobId = jobIdProp ?? routeJobId;
  const embedded = Boolean(jobIdProp);
  const { data: job, isLoading } = useJob(jobId);
  const recompute = useRecomputeJob(jobId ?? "");
  const expandNorms = useExpandNormsJob(jobId ?? "");
  const [soilDialogOpen, setSoilDialogOpen] = useState(false);
  const shareJob = useShareJob(jobId ?? "");
  const setBidVariant = useSetJobBidVariant(jobId ?? "");
  const completeJob = useCompleteJob(jobId ?? "");
  const abandonJob = useAbandonJob(jobId ?? "");
  const submitJob = useSubmitJob(jobId ?? "");
  const setSubmittedAmount = useSetJobSubmittedAmount(jobId ?? "");
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Poziom, z jakim dodajemy KOLEJNĄ osobę (podgląd domyślnie — udostępnienie
  // z edycją ma być świadomym kliknięciem, nie skutkiem ubocznym).
  const [shareWithEdit, setShareWithEdit] = useState(false);
  const shareable = useShareableUsers(shareOpen);

  const [kp, setKp] = useState("");
  const [z, setZ] = useState("");
  const [kz, setKz] = useState("");
  const [rRate, setRRate] = useState("");
  const [downloading, setDownloading] = useState(false);
  const qc = useQueryClient();
  const aiPriceUnmatched = useAiPriceUnmatched();
  const [aiTaskId, setAiTaskId] = useState<string | null>(null);
  const aiTask = useTaskPolling(aiTaskId);

  useEffect(() => {
    const s = aiTask.data?.status;
    if (s && ["success", "failure", "cancelled"].includes(s)) {
      if (s === "success")
        qc.invalidateQueries({ queryKey: ["est", "job", jobId] });
      setAiTaskId(null);
    }
  }, [aiTask.data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDownloadPdf = async () => {
    if (!job) return;
    setDownloading(true);
    try {
      const blob = await downloadJobPdf(job.id);
      downloadBlob(blob, `wycena_${job.id.slice(0, 8)}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (job?.coefficients_snapshot) {
      setKp(job.coefficients_snapshot.kp_pct ?? "");
      setZ(job.coefficients_snapshot.z_pct ?? "");
      setKz(job.coefficients_snapshot.kz_pct ?? "");
      setRRate(job.coefficients_snapshot.r_rate_avg ?? "");
    }
  }, [job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !job)
    return (
      <div className="p-6 text-sm text-[var(--color-text-secondary)]">
        Ładowanie…
      </div>
    );

  const analyzing = job.status === "analyzing" || job.status === "new";
  // Udostępniona do PODGLĄDU → widzę całość (pozycje, scenariusze, marża), ale
  // niczego nie zmieniam. Udostępniona do EDYCJI → mogę wszystko poza dalszym
  // udostępnianiem (can_share zostaje przy autorze i CEO). can_edit to dokładnie
  // ta sama bramka, którą backend stawia na akcjach zmieniających wycenę.
  const canEdit = job.can_edit;
  // The coefficient editor (Kp/Z/Kz/stawka R) and the R·M·S split only matter for
  // the detailed method. With simplified pricing the BCJ unit price already includes
  // every cost + markup, so editing coefficients has no effect — hide it.
  const hasDetailed = job.estimates.some((e) => e.method === "detailed");
  // Wariant, który składamy jako ofertę. Wszystkie kwoty na stronie (pozycje,
  // dowody, podział wg kategorii, marża) idą za tym wyborem — backend liczy tak
  // samo, więc raport PDF i analityka pokazują to samo, co widać tutaj.
  const variant: Variant = job.bid_variant ?? "avg";
  // A przedmiar still priced the simplified way can be expanded from the KNR/KNNR
  // norm catalog into a kosztorys nakładczy on demand (no re-upload).
  const hasSimplified = job.estimates.some((e) => e.method === "simplified");
  // Any position/input still without a price (no catalog match, no manual, no AI
  // price, not investor) → highlight the bulk AI-pricing button red + pulsing.
  const hasUnmatched = job.estimates.some((e) =>
    e.method === "detailed"
      ? e.positions.some(
          (p) =>
            p.inputs.some(
              (i) =>
                i.match_status === "unmatched" &&
                !i.investor_supplied &&
                !i.is_percent_aux &&
                i.ai_price == null &&
                i.manual_price == null,
            ) ||
            // Pozycja bez nakładów wycenia się tylko ceną na poziomie pozycji —
            // bez niej idzie do sumy jako 0 zł.
            (p.inputs.length === 0 && !p.is_manual && p.ai_price == null),
        )
      : e.positions.some(
          (p) => p.match_status === "unmatched" && p.ai_price == null,
        ),
  );

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Nagłówek, a POD nim dwa wiersze akcji: statusy wyceny osobno, narzędzia
          osobno. Wszystko w jednym pasku nagłówka rozjeżdżało stronę — kilkanaście
          przycisków obok tytułu nie miało się gdzie zawinąć. */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
              Wynik wyceny
            </h1>
            {!analyzing && (
              <span
                className={`px-2 py-0.5 text-xs font-medium ${
                  job.outcome
                    ? OUTCOME_STYLE[job.outcome].badge
                    : job.is_abandoned
                      ? "bg-slate-200 text-slate-700"
                      : job.status === "failed"
                        ? "bg-red-100 text-red-800"
                        : job.is_submitted
                          ? "bg-sky-100 text-sky-800"
                          : job.is_completed
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-amber-100 text-amber-800"
                }`}
                title="Status wyceny — GOTOWE, ZŁOŻONY, NIE STARTUJEMY i wynik przetargu ustawiasz jawnie przyciskami"
              >
                {job.status_display}
              </span>
            )}
          </div>
          <Link
            to="/estimating"
            className="text-sm text-[var(--color-primary)] hover:underline"
          >
            ← Nowa wycena
          </Link>
        </div>

        {/* WIERSZ 1 — status wyceny: co my z nią zrobiliśmy (GOTOWE / ZŁOŻONY /
            NIE STARTUJEMY) i jak skończył się przetarg. Cały wiersz istnieje tylko
            dla tych, którzy mogą wycenę zmieniać — udostępniona do podglądu nie. */}
        {canEdit && !analyzing && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Decyzja „Nie startujemy" nie zależy od tego, czy analiza się udała —
                rezygnujemy też z przetargu, którego wyceny nie dokończyliśmy. */}
            {!job.is_submitted && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => abandonJob.mutate(!job.is_abandoned)}
                loading={abandonJob.isPending}
                title={
                  job.is_abandoned
                    ? "Cofnij decyzję — wracamy do statusu wynikającego z wyceny"
                    : "Oznacz, że nie składamy oferty w tym przetargu (wycena zostaje)"
                }
              >
                {job.is_abandoned ? "Jednak startujemy" : "NIE STARTUJEMY"}
              </Button>
            )}
            {!job.is_abandoned && job.status !== "failed" && (
              <>
                {job.is_completed && (
                  <Button
                    variant={job.is_submitted ? "secondary" : "primary"}
                    size="sm"
                    onClick={() => {
                      // Przy składaniu pytamy o kwotę (wariant / kwota z formularza
                      // ofertowego); cofnięcie nie ma o co pytać.
                      if (job.is_submitted) submitJob.mutate({ submitted: false });
                      else setSubmitDialogOpen(true);
                    }}
                    loading={submitJob.isPending && job.is_submitted}
                    title={
                      job.is_submitted
                        ? "Cofnij wycenę do statusu „Gotowy” (oferta jednak niezłożona)"
                        : "Oznacz ofertę jako złożoną w przetargu — zapytamy, z jaką kwotą"
                    }
                  >
                    {job.is_submitted ? "Cofnij „Złożony”" : "ZŁOŻONY"}
                  </Button>
                )}
                {!job.is_submitted && (
                  <Button
                    variant={job.is_completed ? "secondary" : "primary"}
                    size="sm"
                    onClick={() => completeJob.mutate(!job.is_completed)}
                    loading={completeJob.isPending}
                    title={
                      job.is_completed
                        ? "Cofnij wycenę do statusu „W przygotowaniu”"
                        : "Oznacz wycenę jako skończoną (status „Gotowy” na liście)"
                    }
                  >
                    {job.is_completed ? "Wznów pracę" : "GOTOWE"}
                  </Button>
                )}
                {/* Jak skończył się przetarg — dopiero gdy jest co rozstrzygać
                    (wycena skończona albo oferta złożona). Na końcu wiersza, żeby
                    przy zawijaniu podpis „Wynik przetargu" nie odkleił się od
                    swoich przycisków. */}
                {(job.is_completed || job.is_submitted || job.outcome) && (
                  <OutcomePicker job={job} />
                )}
              </>
            )}
          </div>
        )}

        {/* WIERSZ 2 — narzędzia wyceny (udostępnianie, przeliczenia, eksport). */}
        <div className="flex flex-wrap items-center gap-2">
          {job.can_share && (
            <div className="relative">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShareOpen((o) => !o)}
                title="Udostępnij tę wycenę innemu pracownikowi — do podglądu albo z prawem edycji"
              >
                Udostępnij{job.shared_with.length > 0 ? ` (${job.shared_with.length})` : ""}
              </Button>
              {/* Panel otwiera się w PRAWO — przycisk stoi teraz przy lewej
                  krawędzi paska narzędzi, więc „right-0" wypychało go poza stronę. */}
              {shareOpen && (
                <div className="absolute left-0 z-20 mt-1 w-72 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 shadow-lg text-sm">
                  <div className="font-medium mb-1">Udostępnij wycenę</div>
                  {job.shared_with.length > 0 && (
                    <ul className="mb-2 space-y-1">
                      {job.shared_with.map((s) => (
                        <li key={s.id} className="flex items-center gap-2">
                          <span className="flex-1 truncate">{s.name}</span>
                          {/* Poziom przestawia się w miejscu — ten sam POST, więc
                              nie trzeba najpierw usuwać osoby z listy. */}
                          <select
                            className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-xs px-1 py-0.5"
                            value={s.can_edit ? "edit" : "view"}
                            disabled={shareJob.isPending}
                            title="Podgląd = widzi całość, nic nie zmienia. Edycja = może przejąć wycenę i wprowadzać zmiany (poza udostępnianiem dalej)."
                            onChange={(e) =>
                              shareJob.mutate({
                                userId: s.id,
                                canEdit: e.target.value === "edit",
                              })
                            }
                          >
                            <option value="view">podgląd</option>
                            <option value="edit">edycja</option>
                          </select>
                          <button
                            className="text-red-600 hover:underline text-xs"
                            onClick={() => shareJob.mutate({ userId: s.id, remove: true })}
                          >
                            usuń
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <label className="mb-1 flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
                    <input
                      type="checkbox"
                      checked={shareWithEdit}
                      onChange={(e) => setShareWithEdit(e.target.checked)}
                    />
                    z prawem edycji
                  </label>
                  <select
                    className="w-full border rounded px-1.5 py-1 bg-[var(--color-bg-surface)] dark:border-gray-600"
                    defaultValue=""
                    disabled={shareJob.isPending || shareable.isLoading}
                    onChange={(e) => {
                      if (e.target.value) {
                        shareJob.mutate({
                          userId: e.target.value,
                          canEdit: shareWithEdit,
                        });
                        e.target.value = "";
                      }
                    }}
                  >
                    <option value="">+ dodaj osobę…</option>
                    {(shareable.data ?? [])
                      .filter((u) => !job.shared_with.some((s) => s.id === u.id))
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                  </select>
                  <div className="mt-1 text-[11px] text-[var(--color-text-muted)]">
                    Widać tylko osoby z dostępem do wycen przedmiarowych.
                    Udostępniać dalej i usunąć wycenę może wyłącznie autor.
                  </div>
                </div>
              )}
            </div>
          )}
          {hasSimplified && canEdit && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => expandNorms.mutate()}
              loading={expandNorms.isPending}
              disabled={analyzing}
              title="Rozwiń przedmiar z norm KNR/KNNR na kosztorys nakładczy (R/M/S + narzuty). Pozycje bez pewnego dopasowania zostaną oznaczone do ręcznej wyceny."
            >
              Rozwiń do nakładczego
            </Button>
          )}
          {!hasSimplified && canEdit && (
            <Button
              variant="secondary"
              size="sm"
              disabled={analyzing || job.estimates.length === 0}
              title="Zmień kategorię gruntu (I-II / III / IV) we wszystkich pozycjach ziemnych naraz — z podglądem „przed / po” (wartości, nakłady, sprzęt) do zatwierdzenia przed zapisem"
              onClick={() => setSoilDialogOpen(true)}
            >
              Kategoria gruntu…
            </Button>
          )}
          {canEdit && (
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                // Ask before spending: each material is a web search, billed per
                // request on top of tokens, and a big kosztorys is not obviously big
                // from this screen.
                const p = await aiPricePreview(job.id);
                if (p.to_price === 0) {
                  window.alert("Brak pozycji do wyceny rynkowej.");
                  return;
                }
                const skippedNote =
                  p.skipped > 0
                    ? `\n\nLimit ${p.cap} materiałów na jedno uruchomienie — ${p.skipped} zostanie pominiętych. Uruchom ponownie, aby wycenić resztę.`
                    : "";
                const ok = window.confirm(
                  `Wycena rynkowa AI: ${p.to_price} materiałów (${p.positions} pozycji), ` +
                    `do ${p.max_searches} wyszukiwań w internecie.\n` +
                    `Szacowany koszt ≈ $${Number(p.estimated_cost_usd).toFixed(2)}.` +
                    `${skippedNote}\n\nKontynuować?`,
                );
                if (!ok) return;
                const r = await aiPriceUnmatched.mutateAsync(job.id);
                setAiTaskId(r.task_id);
              }}
              loading={!!aiTaskId || aiPriceUnmatched.isPending}
              disabled={analyzing || job.estimates.length === 0}
              className={
                hasUnmatched && !aiTaskId
                  ? "animate-pulse !border-red-500 !text-red-600 !bg-red-50"
                  : ""
              }
              title="AI rekomenduje ceny rynkowe (web search + źródła) dla wszystkich niedobranych pozycji"
            >
              {aiTaskId
                ? "Wyceniam…"
                : hasUnmatched
                  ? "⚠ Wyceń niedobrane AI"
                  : "Wyceń niedobrane AI"}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={onDownloadPdf}
            loading={downloading}
            disabled={analyzing || job.estimates.length === 0}
            title="Pobierz rozwinięty raport PDF z ostrzeżeniami (niedobrane / niepewne / ręczne / inwestorskie)"
          >
            Pobierz PDF
          </Button>
        </div>
      </div>

      {!canEdit && (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3 text-sm text-[var(--color-text-secondary)]">
          <strong>Wycena udostępniona Tobie do odczytu.</strong> Widzisz całość —
          pozycje, scenariusze podwykonawcze i marżę. Żeby móc wprowadzać zmiany,
          poproś autora{job.created_by_name ? ` (${job.created_by_name})` : ""} o
          udostępnienie z prawem edycji.
        </div>
      )}
      {canEdit && !job.can_share && (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3 text-sm text-[var(--color-text-secondary)]">
          <strong>Wycena udostępniona Tobie do edycji.</strong> Możesz ją przejąć i
          zmieniać jak własną. Autorem zostaje
          {job.created_by_name ? ` ${job.created_by_name}` : " autor"} — tylko on
          udostępnia ją dalej i może ją usunąć.
        </div>
      )}

      {/* Provenance banner — the FIRST thing you see: are we pricing a real
          kosztorys nakładczy the client gave us, or a bare przedmiar we expanded? */}
      {!analyzing && job.estimates.length > 0 && (() => {
        const sources = new Set(job.estimates.map((e) => e.naklady_source));
        if (sources.has("norms")) {
          return (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <strong>Źródło nakładów: z OBMIARU (przedmiaru).</strong> Nakłady R/M/S nie
              były w dokumencie — rozwinęliśmy je z obmiaru przez normy KNR/KNNR (nasza
              derywacja). Zweryfikuj, zwłaszcza pozycje o dziwnie niskiej robociźnie i te
              bez dopasowania (0 zł).
            </div>
          );
        }
        if (sources.has("document") && sources.size === 1) {
          return (
            <div className="rounded-md border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">
              <strong>Źródło nakładów: PEŁNY kosztorys nakładczy (z dokumentu).</strong>{" "}
              Nakłady R/M/S pochodzą z przesłanego kosztorysu — liczymy je 1:1, tylko
              podstawiamy ceny.
            </div>
          );
        }
        return (
          <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-3 text-sm text-[var(--color-text-secondary)]">
            <strong>Źródło: obmiar, metoda uproszczona.</strong> Wartość = cena
            jednostkowa BCJ × obmiar (bez rozbicia R/M/S). Kliknij „Rozwiń do
            nakładczego”, by policzyć nakładczo z norm KNR/KNNR.
          </div>
        );
      })()}

      {analyzing && (
        <div className="card p-4 text-sm text-[var(--color-text-secondary)]">
          Analiza w toku… strona odświeży się automatycznie.
        </div>
      )}

      {/* Termin złożenia oferty + link do przetargu — wpisywane z ręki przy wgrywaniu
          pliku. W warsztacie przetargu nie mają racji bytu: jedno i drugie stoi
          w nagłówku obok, a wycena z rysunku i tak bierze je z przetargu. */}
      {!embedded && <JobTenderInfoCard job={job} />}

      {/* Estimator's free-text note (myśl o przetargu, co dokończyć). */}
      {!analyzing && <JobNoteCard job={job} />}

      {/* Warunki wyłączeń zasilania ze specyfikacji — ograniczenie organizacji
          robót, które trzeba znać PRZED wyceną, więc stoi nad kwotami. */}
      {!analyzing && <TakeoffReportCard job={job} />}
      {!analyzing && <OutagesCard job={job} canEdit={canEdit} />}
      {!analyzing && <DeviceCheckCard job={job} canEdit={canEdit} />}

      {/* Trwały audyt zmian podstaw/kategorii gruntu + wycofanie. */}
      {!analyzing && <ChangeHistoryCard jobId={job.id} canEdit={canEdit} />}

      {canEdit && (
        <SoilCascadeDialog
          jobId={job.id}
          open={soilDialogOpen}
          onClose={() => setSoilDialogOpen(false)}
        />
      )}

      {canEdit && (
        <SubmitOfferDialog
          open={submitDialogOpen}
          onClose={() => setSubmitDialogOpen(false)}
          totals={job.totals_snapshot}
          currentVariant={variant}
          pending={submitJob.isPending}
          error={submitJob.isError}
          onConfirm={(v, amount) =>
            submitJob.mutate(
              { submitted: true, variant: v, amount },
              { onSuccess: () => setSubmitDialogOpen(false) },
            )
          }
        />
      )}

      {/* Summary — trzy warianty ceny; kliknięcie wybiera ten, który składamy. */}
      {job.totals_snapshot?.avg && (
        <div>
          <h2 className="section-label mb-2">Wartość przetargowa</h2>
          <div className="grid grid-cols-3 gap-3">
            {VARIANTS.map((v) => (
              <SummaryCard
                key={v.key}
                label={v.label}
                t={job.totals_snapshot[v.key]}
                highlight={variant === v.key}
                showRMS={hasDetailed}
                pending={setBidVariant.isPending}
                onSelect={
                  canEdit ? () => setBidVariant.mutate(v.key) : undefined
                }
                submittedAmount={
                  variant === v.key && job.is_submitted
                    ? // Wyceny złożone przed powstaniem tego pola nie mają kwoty —
                      // wtedy wpisywalny start to policzony wariant, jak przy nowych.
                      (job.submitted_amount ??
                      job.totals_snapshot[v.key]?.total ??
                      "0")
                    : undefined
                }
                onSubmittedAmountSave={
                  canEdit ? (val) => setSubmittedAmount.mutate(val) : undefined
                }
                savingAmount={setSubmittedAmount.isPending}
              />
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-[var(--color-text-muted)]">
            {job.is_submitted
              ? "Oferta złożona — kwotę na czerwono możesz poprawić ręcznie (np. zaokrąglenie z formularza ofertowego); puste pole wraca do kwoty policzonego wariantu. Reszta strony liczy się dalej z wariantu."
              : canEdit
                ? "Kliknij kwotę, żeby wybrać wariant oferty — narzuty (Kp, Z, Kz) są w każdym z nich, a podział kosztów, zł/mb, marża na podwykonawcach i raport PDF przeliczą się z wybranego."
                : `Jako ofertę złożono wariant: ${
                    VARIANTS.find(
                      (v) => v.key === variant,
                    )?.label.toLowerCase() ?? "średnia"
                  }.`}
          </p>
          {setBidVariant.isError && (
            <p className="mt-1 text-[11px] text-red-600">
              Nie udało się przestawić wariantu — spróbuj ponownie.
            </p>
          )}
          {setSubmittedAmount.isError && (
            <p className="mt-1 text-[11px] text-red-600">
              Nie udało się zapisać kwoty oferty — sprawdź format (np. 120117,97)
              i spróbuj ponownie.
            </p>
          )}
        </div>
      )}

      {/* Cost split by category + route zł/mb. */}
      <CategoryBreakdown job={job} />

      {/* Coefficient editor / method note — only once a result exists; hidden while
          analysis is in progress so we don't falsely claim "metoda uproszczona". */}
      {job.estimates.length > 0 &&
        (hasDetailed ? (
        <div className="card p-4 flex flex-wrap items-end gap-3">
          <Field label="Kp [%]" value={kp} onChange={setKp} disabled={!canEdit} />
          <Field label="Z [%]" value={z} onChange={setZ} disabled={!canEdit} />
          <Field label="Kz [%]" value={kz} onChange={setKz} disabled={!canEdit} />
          <Field
            label="Stawka R [zł/r-g]"
            value={rRate}
            onChange={setRRate}
            disabled={!canEdit}
          />
          {canEdit && (
            <Button
              onClick={() =>
                recompute.mutate({
                  kp_pct: kp,
                  z_pct: z,
                  kz_pct: kz,
                  r_rate_avg: rRate,
                })
              }
              loading={recompute.isPending}
            >
              Przelicz
            </Button>
          )}
        </div>
      ) : (
        <div className="text-xs p-4 border border-amber-300 bg-amber-50 text-amber-800">
          ⚠ <span className="font-semibold">Uwaga — metoda uproszczona.</span>{" "}
          Wartość ={" "}
          <span className="font-mono">
            cena jednostkowa SEKOCENBUD (BCJ) × obmiar
          </span>
          . Ta wycena{" "}
          <span className="font-semibold">
            NIE zawiera zysku (Z), narzutów / kosztów pośrednich (Kp) ani
            kosztów zakupu (Kz)
          </span>{" "}
          — dolicz je do wartości ofertowej we własnym zakresie. Pełną,
          edytowalną kontrolę narzutów daje metoda szczegółowa (wymaga cennika
          stawek czynników R/M/S, np. SEKOCENBUD RMS).
        </div>
        ))}

      {/* Page classification per document — collapsed by default. */}
      <DocumentsSection documents={job.documents} sourceFiles={job.files} />

      {/* Estimates */}
      <div className="space-y-4">
        {job.estimates.length === 0 && !analyzing && (
          <p className="text-sm text-[var(--color-text-secondary)]">
            W żadnym z wgranych plików nie wykryto kosztorysu nakładczego.
          </p>
        )}
        {job.estimates.map((est) => (
          <EstimateBlock
            key={est.id}
            est={est}
            ctx={{
              jobId: job.id,
              rmsCatalogId: job.rms_catalog,
              bcjCatalogId: job.bcj_catalog,
              canEdit,
              kpPct: job.coefficients_snapshot?.kp_pct ?? "",
              zPct: job.coefficients_snapshot?.z_pct ?? "",
              variant,
            }}
          />
        ))}
      </div>

      {/* Zestawienie materiałów (z cenami + rozeznaniem rynku) i sprzętu (m-g). */}
      {!analyzing && job.estimates.length > 0 && (
        <SummaryTables
          jobId={job.id}
          canEdit={canEdit}
          hasDetailed={hasDetailed}
        />
      )}

      {/* Subcontractor pricing (Layer B) + margin vs our valuation (Layer A). */}
      {!analyzing && job.estimates.length > 0 && (
        <SubcontractorSection job={job} canEdit={canEdit} />
      )}

      {/* Headless AI review ("presetowany ARON") — risks/inconsistencies on top of the
          deterministic warnings. */}
      {!analyzing && job.estimates.length > 0 && (
        <AiReviewPanel job={job} canEdit={canEdit} />
      )}

      {/* Recognized investor-supplied deliveries — review / restore. */}
      <InvestorList job={job} canEdit={canEdit} />

      {/* Catalog browser — search BCJ / RMS price catalogs. */}
      <CatalogBrowser />

      {/* AI analysis cost + model. */}
      <AiCostBar job={job} />
    </div>
  );
}

// Termin złożenia oferty + link do przetargu. Wpisywane z ręki przy wgrywaniu
// plików, tu poprawiane. Zapis jawnym przyciskiem, jak przy notatce.
function JobTenderInfoCard({ job }: { job: EstimateJobDetail }) {
  const save = useSetJobTenderInfo(job.id);
  const [deadline, setDeadline] = useState(toDateTimeLocal(job.offer_deadline));
  const [url, setUrl] = useState(job.tender_url ?? "");
  useEffect(() => {
    setDeadline(toDateTimeLocal(job.offer_deadline));
    setUrl(job.tender_url ?? "");
  }, [job.id, job.offer_deadline, job.tender_url]);
  const dirty =
    deadline !== toDateTimeLocal(job.offer_deadline) || url !== (job.tender_url ?? "");
  const canEdit = job.can_edit; // autor, CEO lub udostępnienie z edycją

  if (!canEdit) {
    if (!job.offer_deadline && !job.tender_url) return null;
    return (
      <div className="card p-4 space-y-1">
        <h2 className="section-label">Przetarg</h2>
        {job.offer_deadline && (
          <div className={`text-sm ${deadlineToneClass(job.offer_deadline)}`}>
            Termin złożenia: {deadlineLabel(job.offer_deadline)}
          </div>
        )}
        {job.tender_url && (
          <a
            href={job.tender_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[var(--color-primary)] hover:underline break-all"
          >
            {job.tender_url} ↗
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="card p-4 space-y-2">
      <h2 className="section-label">Przetarg</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-[var(--color-text-secondary)] space-y-1">
          <span>Termin złożenia oferty</span>
          <input
            type="datetime-local"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1 text-[var(--color-text-primary)]"
          />
          {job.offer_deadline && !dirty && (
            <span className={`block text-xs ${deadlineToneClass(job.offer_deadline)}`}>
              {deadlineLabel(job.offer_deadline)}
            </span>
          )}
        </label>
        <label className="text-sm text-[var(--color-text-secondary)] space-y-1">
          <span>Link do przetargu</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://platformazakupowa.pl/…"
            className="w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1 text-[var(--color-text-primary)]"
          />
          {job.tender_url && !dirty && (
            <a
              href={job.tender_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs text-[var(--color-primary)] hover:underline"
            >
              otwórz ogłoszenie ↗
            </a>
          )}
        </label>
      </div>
      {dirty && (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() =>
              save.mutate({
                offer_deadline: deadline || null,
                tender_url: url.trim(),
              })
            }
            loading={save.isPending}
          >
            Zapisz
          </Button>
          <button
            className="text-xs text-[var(--color-text-secondary)] underline"
            onClick={() => {
              setDeadline(toDateTimeLocal(job.offer_deadline));
              setUrl(job.tender_url ?? "");
            }}
          >
            Anuluj
          </button>
          {save.isError && (
            <span className="text-xs text-red-500">
              Nie udało się zapisać — sprawdź termin i link.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Notatka kosztorysanta do przetargu — co uwzględnić / dokończyć. Zapis jawnym
// przyciskiem (nie autosave), żeby nie strzelać requestem po każdej literze.
function JobNoteCard({ job }: { job: EstimateJobDetail }) {
  const setNote = useSetJobNote(job.id);
  const [text, setText] = useState(job.note ?? "");
  useEffect(() => {
    setText(job.note ?? "");
  }, [job.id, job.note]);
  const dirty = text !== (job.note ?? "");
  const canEdit = job.can_edit; // autor, CEO lub udostępnienie z edycją
  if (!canEdit && !job.note) return null;
  return (
    <div className="card p-4 space-y-2">
      <h2 className="section-label">Notatka do przetargu</h2>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        disabled={!canEdit}
        placeholder="Twoja myśl o przetargu — co uwzględnić / dokończyć w tej wycenie…"
        className="w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1.5 text-[var(--color-text-primary)] disabled:opacity-70"
      />
      {canEdit && dirty && (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => setNote.mutate(text)}
            loading={setNote.isPending}
          >
            Zapisz notatkę
          </Button>
          <button
            className="text-xs text-[var(--color-text-secondary)] underline"
            onClick={() => setText(job.note ?? "")}
          >
            Anuluj
          </button>
          {setNote.isError && (
            <span className="text-xs text-red-500">Nie udało się zapisać.</span>
          )}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="text-xs text-[var(--color-text-secondary)] flex flex-col gap-1">
      {label}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-sm px-2 py-1 w-28 disabled:opacity-70"
      />
    </label>
  );
}
